/**
 * The CRM workspace's values (design salesVals L20936-21675: crm*, c*, mx*), built from
 * the live routes instead of the design's session state. The markup (CrmView.tsx) reads
 * only this object, key for key with the design.
 *
 * Where the design kept a sample in the browser (its catalog generator, its started-deal
 * list, its mailbox simulation), this reads the server: /api/aithora/crm/* for My day,
 * Signals, counts and the catalog, /api/crm/* for deals, stages, activities, contacts and
 * saved views. Writes go through the same routes and the server enforces every rule again
 * (deal ownership, the Lead exit gate, the Lost reasons, Partner read-only).
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent, type DragEvent, type KeyboardEvent, type MouseEvent } from 'react';
import {
  CRM_BULK_CAP,
  CRM_COMPANY_STATUSES,
  CRM_CONTACTS_EXPORT_PATH,
  CRM_FACILITY_EXPORT_PATH,
  crmActivities,
  crmActivityComplete,
  crmActivityCreate,
  crmBulkStart,
  crmCompany,
  crmContact,
  crmContactArchive,
  crmContactCreate,
  crmContacts,
  crmContactsImport,
  crmContactUpdate,
  crmCounts,
  crmCustomerHealth,
  crmDealCreate,
  crmDealLost,
  crmDealMove,
  crmDealReassign,
  crmDeals,
  crmDealSnooze,
  crmDealUpdate,
  crmFacilityCall,
  crmFacilityFacets,
  crmFacilityParse,
  crmFacilitySearch,
  crmGmailAccounts,
  crmGmailConnect,
  crmGmailDisconnect,
  crmMailDismiss,
  crmMailLink,
  crmMailQueue,
  crmMailSettings,
  crmMailSettingsSave,
  crmMailWaiting,
  crmMailWaitingDone,
  crmMailWaitingSnooze,
  crmNeverSyncAdd,
  crmNeverSyncRemove,
  crmThreadSummary,
  crmThreadSummaryAccept,
  crmThreadSummaryDismiss,
  crmThreadSummaryDraft,
  crmMembers,
  crmMyDay,
  crmOperators,
  crmPipelines,
  crmProspects,
  crmViewCreate,
  crmViews,
  downloadCsv,
  crmBulkReassign,
  crmCompaniesImport,
  crmContactRestore,
  CRM_DEALS_EXPORT_PATH,
  repActivity,
  type ApiError,
  type CatalogFilters,
  type CatalogRow,
  type CatalogSearchResponse,
  type CrmActivity,
  type CrmContact,
  type CrmDeal,
  type CrmDealPatch,
  type CrmPipeline,
  type CrmSavedView,
  type MyDayDeal,
  type CrmOperatorRow,
  type MailVisibility,
  type OperatorSortKey,
} from '@aithora/shared';
import { useSession } from '../../../session/SessionContext';
import { useQuery } from '../../../useQuery';
import { exportCsv } from '../../../ui/exportCsv';
import { plusK } from '../../intelligence/common';
import { useSales } from '../context';
import { BTN, bulkLine, sameView, useBulk, viewStateOf, type BulkKind, type ViewState } from './CrmExtras';
import {
  LOST_REASONS,
  STAGE_COLOR,
  k10,
  MONO,
  NEXT,
  ago,
  btn,
  cap,
  capt,
  cell,
  chip,
  disabledWrite,
  fig,
  firstName,
  ghost,
  ghostToday,
  k,
  k1Yr,
  kYr,
  localDay,
  mailHref,
  offToday,
  prim,
  primDeal,
  primToday,
  stageKey,
  sub,
  title,
} from './util';

/* eslint-disable @typescript-eslint/no-explicit-any */
export type CrmVals = Record<string, any>;

export const RAIL_VIEWS = ['My day', 'All deals', 'Companies', 'People', 'Customers', 'Health', 'Snoozed', 'Lost'] as const;
export type RailView = (typeof RAIL_VIEWS)[number];
const DEAL_VIEWS: RailView[] = ['All deals', 'Snoozed', 'Lost'];
/** The deals table's columns (design L21262: the first seven); Tags and Close date are
 *  production columns a person can add (crm-extras). Facility always shows. */
export type DealCol = 'facility' | 'stage' | 'owner' | 'value' | 'last' | 'next' | 'meet' | 'tags' | 'close';
export const DEAL_COLS: Array<{ key: DealCol; n: string; w: string }> = [
  { key: 'facility', n: 'Facility', w: 'minmax(0,1.6fr)' },
  { key: 'stage', n: 'Stage', w: '124px' },
  { key: 'owner', n: 'Owner', w: '84px' },
  { key: 'value', n: 'Value', w: '96px' },
  { key: 'last', n: 'Last contact', w: '112px' },
  { key: 'next', n: 'Next step', w: 'minmax(0,1.3fr)' },
  { key: 'meet', n: 'Next meeting', w: '100px' },
  { key: 'tags', n: 'Tags', w: 'minmax(0,1fr)' },
  { key: 'close', n: 'Close date', w: '96px' },
];
export const DEFAULT_DEAL_COLS: DealCol[] = ['facility', 'stage', 'owner', 'value', 'last', 'next', 'meet'];
/** People rows per page (design PAGE = 8). */
export const PEOPLE_PAGE = 8;
/** Catalog rows per load (design cLimit 40; the server's page). */
export const CATALOG_PAGE = 40;
/** Under this grid width the right panel becomes an overlay (design crmGridRef, < 900). */
export const CRM_NARROW_PX = 900;
/** The Founding Facility Program's size (handoff 2026-09-28: "50 founding facilities"). */
export const FOUNDING_SLOTS = 50;

const OWN_LABEL: Record<string, string> = { pe: 'PE-backed', chain: 'Chain', nonprofit: 'Nonprofit', independent: 'Independent', government: 'Government' };
const tz = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return undefined;
  }
};
const errText = (e: unknown) => (e && typeof e === 'object' && 'message' in e ? String((e as { message: unknown }).message) : 'That did not go through.');

const phoneFmt = (p: string | null) => (p ? p.replace(/^(\d{3})(\d{3})(\d{4})$/, '($1) $2-$3') : '');
const CONTACT_KINDS = ['email', 'call', 'meeting'];

/** The deal's modeled or entered value, per year, in dollars (null = unknown). */
function dealDollars(d: CrmDeal, myDay: Map<string, MyDayDeal>): { v: number | null; modeled: boolean } {
  if (d.value?.per_year != null) return { v: d.value.per_year, modeled: d.value.basis === 'modeled' };
  if (d.amount_cents != null) return { v: Math.round(Number(d.amount_cents) / 100), modeled: false };
  const m = myDay.get(d.id);
  if (m?.value.per_year != null) return { v: m.value.per_year, modeled: m.value.basis === 'modeled' };
  return { v: null, modeled: false };
}

export function useCrm(): CrmVals {
  const { client, session } = useSession();
  const sales = useSales();
  const { params, setParam, canWrite, isManager, userId, writeReason } = sales;
  const tok = session?.token;

  // ── state (the design's st.crm* keys; the ones a link can open live in the URL) ──
  const view = (RAIL_VIEWS.find((x) => x === params.get('crmV')) ?? 'My day') as RailView;
  const mode = params.get('crmMode') === 'Board' ? 'Board' : 'Table';
  const scope = params.get('crmScope') === 'Team' ? 'Team' : 'Mine';
  const track = params.get('crmTrack') === 'B' ? 'B' : 'A';
  const selParam = params.get('crmSel');
  const facParam = params.get('crmFac');
  const mxOpen = !!params.get('mxOpen');
  const [q, setQ] = useState('');
  const [flowStage, setFlowStage] = useState('');
  const [panelOpen, setPanelOpen] = useState(false);
  const [lostOpen, setLostOpen] = useState(false);
  const [gate, setGate] = useState<string | null>(null);
  const [actErr, setActErr] = useState<string | null>(null);
  const [dragging, setDragging] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState<string | null>(null);
  const [dropLost, setDropLost] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [narrowLocal, setNarrowLocal] = useState(false);
  // catalog
  const [cf, setCfState] = useState<CatalogFilters>({});
  const [savedOn, setSavedOn] = useState<string | null>(null);
  const [fOpen, setFOpen] = useState(!!params.get('cFOpen'));
  const [sortBy, setSortBy] = useState<{ by: 'name' | 'beds' | 'overall_stars' | 'staffing_stars' | 'penalties' | 'opportunity_score' | 'operator'; dir: 'asc' | 'desc' }>({ by: 'opportunity_score', dir: 'desc' });
  const [group, setGroup] = useState(false);
  const [cSel, setCSel] = useState<Record<string, boolean>>({});
  const [cPick, setCPick] = useState<string | null>(params.get('cPick'));
  const [cKind, setCKind] = useState<'Facilities' | 'Operators'>(params.get('cKind') === 'Operators' ? 'Operators' : 'Facilities');
  const [cPickOp, setCPickOp] = useState<string | null>(null);
  const [saveName, setSaveName] = useState('');
  const [more, setMore] = useState<CatalogSearchResponse['rows']>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  // people
  const [pq, setPq] = useState('');
  const [page, setPage] = useState(1);
  const [role, setRole] = useState<string | null>(null);
  const [pSel, setPSel] = useState<Record<string, boolean>>({});
  const [contactId, setContactId] = useState<string | null>(params.get('crmContact'));
  const [ctEdit, setCtEdit] = useState(false);
  const [ctFields, setCtFields] = useState<Record<string, string>>({});
  const [ctConfirm, setCtConfirm] = useState(false);
  const [ctDraft, setCtDraft] = useState('');
  const [ctKind, setCtKind] = useState<'note' | 'call' | 'email' | 'task'>('note');
  const [recId, setRecId] = useState<string | null>(params.get('crmRec'));
  const [recRefreshed, setRecRefreshed] = useState(false);
  const [recLogged, setRecLogged] = useState<string | null>(null);
  const [bulkNote, setBulkNote] = useState<string | null>(null);
  // Restored from production: the contacts last archived here, so they can be put back.
  const [archived, setArchived] = useState<string[]>([]);
  const cImportRef = useRef<HTMLInputElement | null>(null);
  const [cImportNote, setCImportNote] = useState<string | null>(null);
  const [exported, setExported] = useState(false);
  // mail
  const [blkIn, setBlkIn] = useState('');
  // Operators & owners: the column the server sorts by (null = its default, most sites).
  const [opSort, setOpSort] = useState<{ by: OperatorSortKey; dir: 'asc' | 'desc' } | null>(null);
  // crm-extras: deal selection, sort, columns, filters, inline edits, saved deal views, bulk.
  const [dSel, setDSel] = useState<Record<string, boolean>>({});
  const anchor = useRef<number | null>(null);
  const [dSort, setDSort] = useState<{ by: DealCol; dir: 'asc' | 'desc' } | null>(null);
  const [dCols, setDCols] = useState<DealCol[]>(DEFAULT_DEAL_COLS);
  const [dOwnerF, setDOwnerF] = useState('');
  const [dTagF, setDTagF] = useState('');
  const [colsOpen, setColsOpen] = useState(false);
  const [dealViewOn, setDealViewOn] = useState<string | null>(null);
  const [dealBulk, setDealBulk] = useState<BulkKind | null>(null);
  const [coBulk, setCoBulk] = useState<BulkKind | null>(null);
  const [overrides, setOverrides] = useState<Record<string, Partial<CrmDeal>>>({});
  const defaultApplied = useRef<{ deal: boolean; company: boolean }>({ deal: false, company: false });
  const [mxErr, setMxErr] = useState<string | null>(null);

  // ── data ──
  const counts = useQuery((s) => crmCounts(client, { signal: s }), [client, tok]);
  const myDay = useQuery((s) => crmMyDay(client, { query: { scope: scope === 'Team' ? 'team' : 'mine', tz: tz() }, signal: s }), [client, tok, scope]);
  const pipes = useQuery((s) => crmPipelines(client, { signal: s }), [client, tok]);
  const deals = useQuery((s) => crmDeals(client, { query: { limit: 200 }, signal: s }), [client, tok]);
  const members = useQuery((s) => crmMembers(client, { signal: s }), [client, tok]);
  /** The priority prospects: a started facility's profile and why-call line (the deal panel). */
  const prospects = useQuery((s) => crmProspects(client, { query: { limit: 60 }, signal: s }), [client, tok]);
  const week = useQuery((s) => repActivity(client, { params: { id: 'me' }, query: { days: 7 }, signal: s }), [client, tok], { enabled: !!userId });
  const views = useQuery((s) => crmViews(client, { query: { object: 'company' }, signal: s }), [client, tok]);
  const dealViews = useQuery((s) => crmViews(client, { query: { object: 'deal' }, signal: s }), [client, tok]);
  // Only a view of that object (a server that ignores ?object must not mix them).
  const dealViewList: CrmSavedView[] = (dealViews.data?.views ?? []).filter((x) => x.object === 'deal');
  const isCat = view === 'Companies';
  const catalog = useQuery(
    (s) => crmFacilitySearch(client, { body: { filters: cf, sort: group ? { by: 'operator', dir: 'asc' } : sortBy, limit: CATALOG_PAGE }, signal: s }),
    [client, tok, JSON.stringify(cf), sortBy.by, sortBy.dir, group],
    { enabled: isCat },
  );
  const facets = useQuery((s) => crmFacilityFacets(client, { signal: s }), [client, tok], { enabled: isCat });
  const ops = useQuery(
    (s) => crmOperators(client, { query: { q: cf.q, state: cf.state?.length === 1 ? cf.state[0] : undefined, sort: opSort?.by, dir: opSort?.dir, limit: CATALOG_PAGE }, signal: s }),
    [client, tok, cf.q, cf.state?.join(','), opSort?.by, opSort?.dir],
    { enabled: isCat },
  );
  const people = useQuery(
    (s) => crmContacts(client, { query: { q: pq.trim() || undefined, role: role ?? undefined, limit: PEOPLE_PAGE, offset: (page - 1) * PEOPLE_PAGE }, signal: s }),
    [client, tok, pq, role, page],
    { enabled: view === 'People' },
  );
  const health = useQuery((s) => crmCustomerHealth(client, { signal: s }), [client, tok]);
  const gmail = useQuery((s) => crmGmailAccounts(client, { signal: s }), [client, tok]);
  const mailQ = useQuery((s) => crmMailQueue(client, { signal: s }), [client, tok]);
  // My mailbox settings and my waiting-on-reply list (both per person, never the admin token).
  const mailSet = useQuery((s) => crmMailSettings(client, { signal: s }), [client, tok], { enabled: !!userId });
  const waiting = useQuery((s) => crmMailWaiting(client, { signal: s }), [client, tok], { enabled: !!userId });

  useEffect(() => {
    setMore([]);
    setCursor(null);
  }, [catalog.data]);

  const memberList = members.data?.members;
  const allDeals = deals.data?.deals ?? [];
  const today = myDay.data?.today ?? [];
  const myDayById = useMemo(() => new Map(today.map((d) => [d.deal_id, d])), [today]);
  const pipelines: CrmPipeline[] = pipes.data?.pipelines ?? [];
  const pipelineOf = (id: string | null | undefined) => pipelines.find((p) => p.id === id) ?? null;
  const trackPipe = pipelines.find((p) => (track === 'B' ? p.name === 'Multi-site' : p.is_default && p.type === 'facility')) ?? null;
  const stagesOf = (pid: string | null | undefined) => (pipelineOf(pid)?.stages ?? []).slice().sort((a, b) => a.sort - b.sort);
  const now = Date.now();
  const mayWrite = (d: { owner_id: string | null } | null | undefined) => canWrite && (!d || isManager || (!!userId && d.owner_id === userId));
  const notYours = 'Only the deal owner, a Super Admin or an Operator can change this deal.';
  const writeTitle = (d?: { owner_id: string | null } | null) => (!canWrite ? writeReason ?? undefined : d && !mayWrite(d) ? notYours : undefined);

  const reloadDeals = useCallback(() => {
    myDay.reload();
    deals.reload();
    counts.reload();
    sales.reloadSignals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myDay.reload, deals.reload, counts.reload, sales.reloadSignals]);

  /** Run a write; the server's refusal (gate, not yours, read only) is shown, never hidden. */
  const run = useCallback(
    async (fn: () => Promise<unknown>, after?: () => void) => {
      setActErr(null);
      try {
        await fn();
        after?.();
        reloadDeals();
      } catch (e) {
        setActErr(errText(e));
      }
    },
    [reloadDeals],
  );

  // crm-extras: bulk changes with a 10-second Undo (deals, and companies in the catalog).
  const bulkD = useBulk(client, reloadDeals);
  const reloadCatalog = useCallback(() => catalog.reload(), [catalog.reload]); // eslint-disable-line react-hooks/exhaustive-deps
  const bulkC = useBulk(client, reloadCatalog);

  // ── selection ──
  const openToday = today.filter((d) => !d.snoozed_until);
  const rowsToday = openToday.filter((d) => !flowStage || stageKey(d.stage) === flowStage);
  const selDeal: CrmDeal | null =
    (selParam && allDeals.find((d) => d.id === selParam)) ||
    (facParam && allDeals.find((d) => d.facility_id === facParam && d.status === 'open')) ||
    (view === 'My day' && openToday[0] ? allDeals.find((d) => d.id === openToday[0].deal_id) ?? null : null) ||
    null;
  const selectDeal = (id: string) => {
    setParam('crmSel', id);
    setParam('crmFac', null);
    setPanelOpen(true);
    setLostOpen(false);
  };
  const dealActs = useQuery((s) => crmActivities(client, { query: { dealId: selDeal!.id, limit: 50 }, signal: s }), [client, tok, selDeal?.id], { enabled: !!selDeal });
  // The Atlas draft of the deal's latest thread. Atlas drafts on new mail: when the deal
  // has mail and no current draft, ask once (the server decides who may, and never drafts
  // from a thread that may carry health information).
  const sumQ = useQuery((s) => crmThreadSummary(client, { params: { id: selDeal!.id }, signal: s }), [client, tok, selDeal?.id], { enabled: !!selDeal });
  const drafted = useRef<Set<string>>(new Set());
  useEffect(() => {
    const d = sumQ.data;
    if (!d || !canWrite || !d.has_mail || (d.summary && !d.summary.stale)) return;
    const key = d.deal_id + ':' + (d.summary?.id ?? 'none');
    if (drafted.current.has(key)) return;
    drafted.current.add(key);
    crmThreadSummaryDraft(client, { params: { id: d.deal_id }, body: {} })
      .then((r) => { if (r && (r as { summary?: unknown }).summary) sumQ.reload(); })
      .catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sumQ.data, canWrite, client]);

  // ── rail + grid (L21276-21298) ──
  const narrow = narrowLocal;
  const gridRef = useRef<HTMLDivElement | null>(null);
  const roRef = useRef<ResizeObserver | null>(null);
  const setCrmNarrow = sales.setCrmNarrow;
  const crmGridRef = useCallback(
    (el: HTMLDivElement | null) => {
      roRef.current?.disconnect();
      gridRef.current = el;
      if (!el) return;
      const chk = () => {
        const n = el.clientWidth < CRM_NARROW_PX;
        setNarrowLocal(n);
        setCrmNarrow(n);
      };
      if (typeof ResizeObserver !== 'undefined') {
        roRef.current = new ResizeObserver(chk);
        roRef.current.observe(el);
      }
      chk();
    },
    [setCrmNarrow],
  );
  useEffect(() => () => setCrmNarrow(false), [setCrmNarrow]);

  const isDealV = DEAL_VIEWS.includes(view);
  const other = view === 'People' || view === 'Customers' || view === 'Health' || (isDealV && mode === 'Board');
  const showPanel = view === 'My day' || (isDealV && mode === 'Table') || isCat;
  const panelOn = showPanel && (!narrow || panelOpen);
  const railS = (on: boolean) =>
    'display:flex;align-items:center;gap:6px;padding:6px 8px;border-radius:7px;font-size:16px;text-decoration:none;color:#22272E;font-weight:' + (on ? '700' : '500') + ';' + (on ? 'background:rgba(79,138,214,.13);' : '');
  const c = counts.data;
  const n = (x: number | null | undefined) => (x == null ? '–' : x.toLocaleString('en-US'));
  const railCount: Record<RailView, string> = {
    'My day': c ? n(Math.max(0, c.team_open - c.snoozed)) : '–',
    'All deals': deals.data ? n(allDeals.filter((d) => d.status !== 'lost').length) : '–',
    Companies: c ? (c.catalog < 1000 ? String(c.catalog) : Math.round(c.catalog / 1000) + 'k') : '–',
    People: c ? n(c.contacts) : '–',
    Customers: c ? n(c.customers) : '–',
    Health: health.data ? n(health.data.customers.length) : '–',
    Snoozed: c ? n(c.snoozed) : '–',
    Lost: c ? n(c.lost) : '–',
  };
  const goView = (r: RailView) => {
    setPanelOpen(false);
    setParam('crmV', r === 'My day' ? null : r);
    setSavedOn(null);
    setDealViewOn(null);
    setDSel({});
    setDealBulk(null);
    setDOwnerF('');
    setDTagF('');
    setGate(null);
    setQ('');
  };
  const setCf = (o: CatalogFilters) => {
    setCfState(o);
    setCSel({});
  };

  // ── mailbox (L21343-21406) ──
  const acct = (gmail.data?.accounts ?? []).find((a) => a.userId === userId && a.status === 'active') ?? null;
  const done = !!acct;
  const provName = (p: string) => (/gmail|google/i.test(p) ? 'Google' : p);
  const queue = mailQ.data?.items ?? [];
  const share: MailVisibility = mailSet.data?.visibility ?? 'subject';
  const neverSync = mailSet.data?.never_sync ?? [];
  const alwaysSkipped = mailSet.data?.always_skipped ?? ['aithoracare.com (internal)', 'Newsletters and auto-replies'];
  const waitRows = waiting.data?.rows ?? [];
  const summ = sumQ.data?.summary ?? null;
  const summShown = !!summ && !summ.withheld && summ.status !== 'dismissed';
  const mailRun = (fn: () => Promise<unknown>, after: () => void) => {
    setMxErr(null);
    fn().then(after, (e) => setMxErr(errText(e)));
  };
  const mx = {
    mxChip: done ? provName(acct!.provider) + ' · in sync · ' + (acct!.lastSyncedAt ? ago(acct!.lastSyncedAt).replace(' ago', '') + ' ago' : 'not yet') : 'Connect email & calendar',
    mxChipStyle:
      'display:inline-flex;align-items:center;gap:6px;height:34px;padding:0 12px;border-radius:9px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;' +
      (done ? 'color:#12503C;background:rgba(47,138,111,.12);' : 'color:#22272E;border:1px dashed rgba(34,39,46,.35);'),
    mxOpenBtn: () => setParam('mxOpen', '1'),
    mxClose: () => setParam('mxOpen', null),
    mxOpen,
    mxFooter: done
      ? 'Inbox and calendar synced · ' + (share === 'meta' ? 'team sees who and when' : share === 'subject' ? 'team sees subject lines' : 'team sees full email')
      : 'Inbox not connected · Connect',
    mxNone: !done,
    mxSyncing: false,
    mxDone: done,
    mxPct: '100%',
    mxBar: 'display:block;height:100%;border-radius:3px;background:#2F5C97;transition:width .4s ease;width:100%;',
    mxGoogle: async () => {
      setMxErr(null);
      if (!canWrite) return;
      try {
        const r = await crmGmailConnect(client);
        window.location.assign(r.url);
      } catch (e) {
        setMxErr(errText(e));
      }
    },
    mxMicrosoft: () => setMxErr(mailSet.data?.providers.microsoft.reason ?? 'Microsoft 365 is not connectable yet: only Google Workspace sign-in is built.'),
    mxBtnP:
      'display:inline-flex;align-items:center;justify-content:center;height:30px;padding:0 12px;border-radius:7px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;color:#fff;background:#2F5C97;' +
      (canWrite ? '' : disabledWrite),
    mxBtnG:
      'display:inline-flex;align-items:center;justify-content:center;height:30px;padding:0 12px;border-radius:7px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;color:#22272E;border:1px solid rgba(34,39,46,.2);background:#FCFAF4;' +
      disabledWrite,
    mxAcct: done ? acct!.emailAddress + ' · ' + provName(acct!.provider) : '',
    mxSyncLine: done ? (acct!.lastSyncedAt ? 'Last synced ' + ago(acct!.lastSyncedAt) + '.' : 'Connected. The first import has not run yet.') : 'Importing the last 12 months. You can keep working; this runs in the background.',
    mxDisconnect: () =>
      acct &&
      run(
        () => crmGmailDisconnect(client, { params: { id: acct.id } }),
        () => gmail.reload(),
      ),
    mxShareOpts: [
      ['meta', 'Who and when'],
      ['subject', 'Plus subject line'],
      ['full', 'Full email'],
    ].map((o) => ({
      n: o[1],
      onClick: () => {
        if (!canWrite || !userId || o[0] === share) return;
        mailRun(() => crmMailSettingsSave(client, { body: { visibility: o[0] as MailVisibility } }), () => mailSet.reload());
      },
      style:
        'display:inline-flex;align-items:center;height:28px;padding:0 10px;border-radius:6px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;' +
        (o[0] === share ? 'background:#2F5C97;color:#fff;' : 'color:#3C454F;background:rgba(34,39,46,.06);') +
        (canWrite && userId ? '' : 'cursor:default;'),
    })),
    mxShareNote:
      share === 'meta'
        ? 'Teammates see “Andrew ↔ Dana Ruiz · Tue 2:14 PM”. Nothing about what was said.'
        : share === 'subject'
          ? 'Teammates see “Andrew ↔ Dana Ruiz · Tue 2:14 PM · Re: pilot start date”. Bodies stay private to you.'
          : 'Teammates see the whole email and attachments. You can still mark any thread Private.',
    // Always skipped (not removable), then my own never-sync list (removable).
    mxBlock: [
      ...alwaysSkipped.map((b) => ({ n: b, onRm: () => setMxErr(b + ' is always skipped.') })),
      ...neverSync.map((b) => ({
        n: b,
        onRm: () => canWrite && mailRun(() => crmNeverSyncRemove(client, { params: { entry: b } }), () => mailSet.reload()),
      })),
    ],
    mxBlkIn: blkIn,
    mxSetBlk: (e: ChangeEvent<HTMLInputElement>) => setBlkIn(e.target.value),
    mxAddBlk: () => {
      const v = blkIn.trim();
      if (!v || !canWrite) return;
      mailRun(() => crmNeverSyncAdd(client, { body: { entry: v } }), () => {
        setBlkIn('');
        mailSet.reload();
      });
    },
    mxErr,
    mxWaitHas: waitRows.length > 0,
    mxNoWait: waitRows.length === 0,
    mxWaitRows: waitRows.map((w) => ({
      who: w.contact_name ? w.contact_name + (w.contact_title ? ', ' + w.contact_title : '') : w.facility_name ?? w.deal_name ?? 'Unknown contact',
      co: w.facility_name ?? w.deal_name ?? '–',
      kind: w.kind === 'awaiting_them' ? 'They haven’t replied' : 'You haven’t replied',
      age: w.days + (w.days === 1 ? ' day' : ' days'),
      subj: w.subject ?? 'Subject withheld',
      kStyle: 'font-size:14px;font-weight:800;white-space:nowrap;color:' + (w.kind === 'awaiting_them' ? '#8A5A00' : '#B3261E') + ';',
      mail: 'mailto:?subject=' + encodeURIComponent(w.subject ?? '') + '&cc=partner%40aithoracare.com&bcc=deals%40aithoracare.com',
      onSnooze: () => canWrite && mailRun(() => crmMailWaitingSnooze(client, { params: { threadKey: w.thread_key }, body: { days: 2 } }), () => waiting.reload()),
      onDone: () => canWrite && mailRun(() => crmMailWaitingDone(client, { params: { threadKey: w.thread_key }, body: {} }), () => waiting.reload()),
      ghost: 'display:inline-flex;align-items:center;height:26px;padding:0 9px;border-radius:7px;font-size:14px;font-weight:700;text-decoration:none;white-space:nowrap;color:#22272E;border:1px solid rgba(34,39,46,.22);',
      prim: 'display:inline-flex;align-items:center;height:26px;padding:0 9px;border-radius:7px;font-size:14px;font-weight:700;text-decoration:none;white-space:nowrap;color:#fff;background:#2F5C97;',
    })),
    mxWaitHead: waitRows.length + ' waiting on a reply',
    mxQHas: queue.length > 0,
    mxQRows: queue.map((it) => {
      const cand = it.suggestion?.candidates?.[0];
      return {
        from: it.from_addr ?? 'Unknown sender',
        hint: it.reason === 'ambiguous_company' ? 'matches more than one company' : it.suggestion?.domains?.length ? 'domain ' + it.suggestion.domains.join(', ') + ' is not a company in the CRM' : 'no company in the CRM matched',
        hasSug: !!cand && !!(cand.facility_id || cand.company_id),
        sug: cand ? 'Link to ' + title(cand.name ?? '') : '',
        onLink: () =>
          cand &&
          run(
            () => crmMailLink(client, { params: { id: it.id }, body: cand.facility_id ? { facilityId: cand.facility_id } : { companyId: cand.company_id! } }),
            () => mailQ.reload(),
          ),
        onIgnore: () => run(() => crmMailDismiss(client, { params: { id: it.id }, body: {} }), () => mailQ.reload()),
      };
    }),
    mxQLine: queue.length + (queue.length === 1 ? ' sender' : ' senders') + ' could not be matched to a company',
    mxDealConn: done ? 'Not measured yet: connection strength needs the synced inbox scored' : 'Connect email to see connection strength',
    mxSumHas: summShown,
    mxSumAccepted: summShown && summ!.status === 'accepted',
    mxSumPending: summShown && summ!.status === 'draft',
    mxSumText: summShown ? summ!.summary ?? '' : '',
    mxSumTask: summShown && summ!.next_step ? 'Task: ' + summ!.next_step + (summ!.next_date ? ' by ' + summ!.next_date : '') : '',
    mxSumAccept: () =>
      summ && run(() => crmThreadSummaryAccept(client, { params: { id: summ.id }, body: {} }), () => {
        sumQ.reload();
        dealActs.reload();
      }),
    mxSumReject: () => summ && run(() => crmThreadSummaryDismiss(client, { params: { id: summ.id }, body: {} }), () => sumQ.reload()),
  };

  // ── My day (L21025-21148) ──
  const lastOf = (d: MyDayDeal) =>
    d.last_contact_at ? (d.last_contact_kind ? cap(d.last_contact_kind) : 'Contact') + ' · ' + ago(d.last_contact_at, now) : null;
  const pipeOf = (d: MyDayDeal) => allDeals.find((x) => x.id === d.deal_id)?.pipeline_id ?? null;
  const nextStage = (pid: string | null, stageName: string) => {
    const order = stagesOf(pid).filter((s) => !s.is_lost);
    const i = order.findIndex((s) => stageKey(s.name) === stageKey(stageName));
    return i >= 0 && i < order.length - 1 ? order[i + 1] : null;
  };
  const dealOfRow = (d: MyDayDeal) => ({ owner_id: d.owner_id });
  const crmTodayRows = rowsToday.map((d, i) => {
    const l = lastOf(d);
    const ns = nextStage(pipeOf(d), d.stage);
    const gateOn = stageKey(d.stage) === 'LEAD' && !d.last_contact_at;
    const ok = mayWrite(dealOfRow(d));
    const soon = d.meeting === 'today' ? 'Meeting today' : d.meeting === 'tomorrow' ? 'Meeting tomorrow' : '';
    const amt = d.value.per_year;
    return {
      key: d.deal_id,
      name: title(d.name),
      stage: cap(d.stage),
      amount: amt != null ? kYr(amt) : '',
      hasAmt: amt != null,
      modeled: d.value.basis === 'modeled',
      last: l ?? 'No contact logged yet',
      lastStyle: 'font-size:14px;font-weight:600;white-space:nowrap;color:' + (l ? '#54606C' : '#B3261E') + ';',
      next: 'Next: ' + (NEXT[stageKey(d.stage)] || 'Keep it moving'),
      stageStyle:
        'font-size:14px;font-weight:800;letter-spacing:.07em;text-transform:uppercase;border-radius:4px;padding:2px 6px;white-space:nowrap;color:#264C7E;background:rgba(79,138,214,.14);',
      style:
        'display:flex;flex-direction:column;gap:6px;padding:9px 11px;border-radius:9px;cursor:pointer;animation:riseIn .28s cubic-bezier(.22,.61,.36,1) both;animation-delay:' +
        Math.min(240, i * 30) +
        'ms;' +
        (selDeal?.id === d.deal_id ? 'background:rgba(79,138,214,.1);' : 'background:transparent;'),
      onSel: () => selectDeal(d.deal_id),
      // The Email button opens the rep's own mail app; the email files itself when it reaches deals@ (BCC).
      mail: mailHref('Aithora for ' + title(d.facility_name ?? d.name)),
      onEmail: () => selectDeal(d.deal_id),
      onCall: (e?: MouseEvent) => {
        e?.stopPropagation();
        if (!ok) return;
        run(() => crmActivityCreate(client, { body: { type: 'call', subject: 'Call logged', dealId: d.deal_id, facilityId: d.facility_id } }), () => {
          selectDeal(d.deal_id);
          dealActs.reload();
        });
      },
      onSnooze: (e?: MouseEvent) => {
        e?.stopPropagation();
        if (!ok) return;
        run(() => crmDealSnooze(client, { params: { id: d.deal_id }, body: { days: 3 } }));
      },
      onAdvance: (e?: MouseEvent) => {
        e?.stopPropagation();
        if (!ns || gateOn || !ok) return;
        run(() => crmDealMove(client, { params: { id: d.deal_id }, body: { stageId: ns.id } }), () => selectDeal(d.deal_id));
      },
      advLabel: ns ? cap(ns.name) + ' →' : 'Final stage',
      advStyle: ns && !gateOn && ok ? primToday : offToday,
      advTitle: !ok ? writeTitle(dealOfRow(d)) : gateOn ? 'Log a call or email first. A deal cannot leave Lead with no contact.' : ns ? 'Move to ' + cap(ns.name) : '',
      ghost: ghostToday + (ok ? '' : disabledWrite),
      hasMeet: !!soon,
      meetLabel: soon + ' · prep ready',
      onMp: (e?: MouseEvent) => {
        e?.stopPropagation();
        if (d.facility_id) sales.openPrep(d.facility_id);
      },
      meetStyle: 'font-size:14px;font-weight:800;letter-spacing:.06em;text-transform:uppercase;border-radius:4px;padding:2px 6px;white-space:nowrap;color:#8A5A00;background:rgba(165,86,26,.13);',
    };
  });
  const startDeal = (facilityId: string, name: string, source: string) =>
    run(
      () => crmDealCreate(client, { body: { name, facilityId, type: 'facility', track, source } }),
      () => setParam('crmFac', facilityId),
    );
  const notStarted = myDay.data?.not_started ?? [];
  const crmPros = notStarted.map((p, i) => {
    const size = p.beds != null ? p.beds + ' beds' : p.residents != null ? p.residents + ' residents' : null;
    return {
      name: title(p.name),
      meta: [p.type, p.state, size, p.tier ? 'Tier ' + p.tier.value : null].filter(Boolean).join(' · '),
      amount: k1Yr(p.open_dollars.per_year),
      modeled: p.open_dollars.modeled,
      why: p.why ?? '',
      hasWhy: !!p.why,
      flags: (p.flags ?? []).map((f) => ({
        n: f,
        style:
          'display:inline-flex;align-items:center;height:22px;padding:0 8px;border-radius:11px;font-size:14px;font-weight:700;white-space:nowrap;' +
          (/penalt|negative|1★|2★/.test(f) ? 'color:#8E2A1F;background:rgba(179,38,30,.1);' : 'color:#3C454F;background:rgba(34,39,46,.07);'),
      })),
      hasFlags: (p.flags ?? []).length > 0,
      style: 'display:flex;align-items:center;gap:10px;padding:7px 11px;border-radius:9px;animation:riseIn .28s cubic-bezier(.22,.61,.36,1) both;animation-delay:' + Math.min(300, (crmTodayRows.length + i) * 30) + 'ms;',
      onStart: () => canWrite && startDeal(p.facility_id, p.name, 'catalog'),
      ghost: ghostToday + (canWrite ? '' : disabledWrite),
    };
  });
  const strip = myDay.data?.strip;
  const pipeK = strip ? strip.open_pipeline.per_year / 1000 : 0;
  const PST: Array<[string, string]> = [
    ['LEAD', 'Lead'],
    ['QUALIFIED', 'Qualified'],
    ['PILOT', 'Pilot'],
    ['CONTRACT', 'Contract'],
    ['LIVE', 'Live'],
  ];
  const cnt: Record<string, number> = {};
  for (const t of [myDay.data?.stage_counts.A, myDay.data?.stage_counts.B]) for (const s of t?.stages ?? []) cnt[stageKey(s.stage)] = (cnt[stageKey(s.stage)] ?? 0) + s.count;
  const maxC = Math.max(1, ...PST.map((p) => cnt[p[0]] || 0));
  const wk = week.data?.counts;
  const top = crmPros[0];
  const topTot = notStarted.reduce((a, p) => a + p.open_dollars.per_year, 0);
  const myday = {
    crmScopeTabs: ['Mine', 'Team'].map((x) => ({ n: x, onClick: () => setParam('crmScope', x === 'Mine' ? null : x), style: sub(scope === x) })),
    crmActWeek: [
      ['Calls', wk ? wk.dials : null, 'this week'],
      ['Emails filed', wk ? wk.emails_filed : null, 'this week'],
      ['Notes', wk ? wk.notes : null, 'this week'],
      ['Meetings', strip ? strip.meetings_next_7_days : null, 'next 7 days'],
    ].map((x) => ({ k: x[0], v: x[1] == null ? '–' : String(x[1]), sub: x[2] })),
    crmDashK: [
      ['Open pipeline', strip ? (pipeK ? '$' + (pipeK >= 1000 ? (pipeK / 1000).toFixed(2) + 'M' : Math.round(pipeK) + 'k') + '/yr' : '$0') : '–', strip && pipeK && strip.open_pipeline.modeled ? 'MODELED' : ''],
      ['Open deals', strip ? String(strip.open_deals) : '–', ''],
      ['Meetings 7d', strip ? String(strip.meetings_next_7_days) : '–', ''],
      ['Quiet 7d+', strip ? String(strip.quiet_7_days) : '–', ''],
      ['Live', strip ? String(strip.live_facilities) : '–', ''],
    ].map((x) => ({
      k: x[0],
      v: x[1],
      badge: x[2],
      hasBadge: !!x[2],
      vStyle: 'font-size:17px;font-weight:800;font-variant-numeric:tabular-nums;letter-spacing:-.01em;color:' + (String(x[0]).indexOf('Quiet') === 0 && x[1] !== '0' && x[1] !== '–' ? '#8A5A00' : '#22272E') + ';',
    })),
    crmDashFlow: PST.map((p, i) => {
      const cc = cnt[p[0]] || 0;
      const on = flowStage === p[0];
      return {
        n: p[1],
        c: String(cc),
        onClick: () => setFlowStage(on ? '' : p[0]),
        title: on ? 'Click again to show every stage' : 'Show ' + p[1] + ' deals only',
        style:
          'flex:1 1 0;min-width:0;display:flex;flex-direction:column;gap:4px;padding:6px 9px;border-radius:8px;text-decoration:none;color:#22272E;' +
          (on ? 'background:rgba(47,92,151,.12);box-shadow:inset 0 0 0 1px rgba(47,92,151,.4);' : 'background:rgba(34,39,46,.035);'),
        bar:
          'display:block;height:4px;border-radius:2px;background:' +
          (p[0] === 'LIVE' ? '#2F8A6F' : '#4F8AD6') +
          ';width:' +
          (cc ? Math.max(8, Math.round((cc / maxC) * 100)) : 0) +
          '%;transform-origin:left;animation:growW .5s cubic-bezier(.22,.61,.36,1) both;animation-delay:' +
          i * 60 +
          'ms;',
      };
    }),
    crmDashFlowLost: String(cnt.LOST || 0),
    crmDashFlowNote: flowStage ? 'Showing ' + cap(flowStage) + ' only · click again to clear' : 'Click a stage to filter the list',
    crmTodayHead: flowStage
      ? rowsToday.length + ' ' + cap(flowStage) + (rowsToday.length === 1 ? ' deal' : ' deals') + ' · click the stage again to show all'
      : openToday.length
        ? openToday.length + (openToday.length === 1 ? ' open deal' : ' open deals') + ' · oldest contact first'
        : myDay.data ? 'No open deals' : '',
    crmTodayRows,
    crmTodayNone: !!myDay.data && crmTodayRows.length === 0,
    crmPros,
    crmProsHas: crmPros.length > 0,
    crmTopPro: top ? { ...top } : { name: '', meta: '', amount: '', why: '', flags: [], hasWhy: false, hasFlags: false, onStart: () => undefined, modeled: false },
    crmTopProHas: crmPros.length > 0,
    crmTopProNone: crmPros.length === 0,
    crmProsStats: [
      ['Top ' + crmPros.length + ' open', '$' + (topTot / 1e6).toFixed(2) + 'M/yr'],
      ['Deals open', String(openToday.length)],
      ['Started', deals.data ? String(allDeals.length) : '–'],
      ['Email filing', 'partner@ → deals@'],
    ].map((x) => ({ k: x[0], v: x[1] })),
  };

  // ── deal panel (crmD, L21119-21147) ──
  const selStages = selDeal ? stagesOf(selDeal.pipeline_id).filter((s) => !s.is_lost) : [];
  const selMy = selDeal ? myDayById.get(selDeal.id) : undefined;
  const selVal = selDeal ? dealDollars(selDeal, myDayById) : { v: null, modeled: false };
  const selOk = mayWrite(selDeal);
  const acts: CrmActivity[] = dealActs.data?.activities ?? [];
  const sourceLabel: Record<string, string> = { territory: 'Territory', catalog: 'Companies', meeting_prep: 'Meeting prep', mail: 'an email', kit: 'the Sales Kit', manual: 'the CRM' };
  const soonOf = (iso: string | null | undefined) => {
    if (!iso) return '';
    const d = localDay(iso);
    return d === localDay(now) ? 'Meeting today' : d === localDay(now + 864e5) ? 'Meeting tomorrow' : '';
  };
  const kStyle = (kind: string) =>
    'font-size:14px;font-weight:800;letter-spacing:.07em;text-transform:uppercase;border-radius:4px;padding:2px 5px;white-space:nowrap;align-self:flex-start;flex:0 0 auto;' +
    (kind === 'email' || kind === 'task'
      ? 'color:#264C7E;background:rgba(79,138,214,.14);'
      : kind === 'call' || kind === 'meeting'
        ? 'color:#1F6B52;background:rgba(47,138,111,.14);'
        : kind === 'lost'
          ? 'color:#B3261E;background:rgba(179,38,30,.1);'
          : 'color:#54606C;background:rgba(34,39,46,.07);');
  // The design's panel reads its PRIORITY row for a started facility (profile line and why,
  // L21122); the server's version is the priority prospect (store.priorityProspects).
  const pro = selDeal?.facility_id ? (prospects.data?.prospects ?? []).find((p) => p.facilityId === selDeal.facility_id) ?? null : null;
  const proMeta = pro
    ? [pro.type, pro.state, pro.beds != null ? pro.beds + ' beds' : pro.residents ? pro.residents + ' residents' : null, pro.tier ? 'Tier ' + pro.tier : null].filter(Boolean).join(' · ')
    : '';
  // The deal's first history row: it was started at its board's entry stage (the server
  // starts every deal there), when and by whom the deal row says.
  const entry = selDeal ? stagesOf(selDeal.pipeline_id)[0] : undefined;
  const startedRow = selDeal && entry
    ? {
        kind: 'Stage',
        text: 'Deal started at ' + cap(entry.name) + (selDeal.source === 'territory' ? ' from Territory' : ''),
        when: ago(selDeal.created_at, now) + ' · ' + firstName(memberList, selDeal.owner_id),
        kStyle: kStyle('stage'),
      }
    : null;
  const actText = (a: CrmActivity) => (a.phi_suppressed ? 'Not logged: may contain health information' : a.subject || a.body || cap(a.type));
  const crmD = selDeal
    ? {
        name: title(selDeal.name),
        meta: pro ? proMeta : 'Started from ' + (sourceLabel[selDeal.source ?? ''] ?? 'the CRM') + ' · ' + ago(selDeal.created_at, now),
        why: pro?.talkingPoint ?? '',
        hasWhy: !!pro?.talkingPoint,
        amount: selVal.v != null ? k(selVal.v) + '/yr' : 'not modeled',
        modeled: selVal.modeled,
        owner: firstName(memberList, selDeal.owner_id) + (selDeal.owner_id && selDeal.owner_id === userId ? ' (you)' : ''),
        steps: selStages.map((s, i) => {
          const cur = selStages.findIndex((x) => x.id === selDeal.stage_id);
          const lost = selDeal.status === 'lost';
          return {
            n: cap(s.name),
            bar:
              'height:4px;border-radius:2px;background:' +
              (lost ? 'rgba(179,38,30,.35)' : i < cur ? '#2F5C97' : i === cur ? '#4F8AD6' : 'rgba(34,39,46,.12)') +
              ';' +
              (i === cur ? 'animation:pulse 2s ease-in-out infinite;' : ''),
            lab: 'font-size:14px;font-weight:' + (i === cur ? '800' : '600') + ';color:' + (i === cur ? '#22272E' : '#54606C') + ';overflow-wrap:break-word;',
          };
        }),
        stage: selDeal.status === 'lost' ? 'Lost' : cap(selDeal.stage_name),
        timeline: [
          ...acts.map((a) => ({ kind: cap(a.type), text: actText(a), when: ago(a.occurred_at ?? a.created_at, now) + ' · ' + firstName(memberList, a.owner_id), kStyle: kStyle(a.type) })),
          ...(dealActs.data && startedRow ? [startedRow] : []),
        ],
        noTimeline: dealActs.data ? acts.length === 0 && !startedRow : false,
        mail: mailHref('Aithora for ' + title(selDeal.facility_name ?? selDeal.name)),
        onEmail: () => undefined,
        onCall: () => selOk && run(() => crmActivityCreate(client, { body: { type: 'call', subject: 'Call logged', dealId: selDeal.id, facilityId: selDeal.facility_id } }), () => dealActs.reload()),
        onMeet: () =>
          selOk &&
          run(
            () => crmActivityCreate(client, { body: { type: 'meeting', subject: 'Meeting held', dealId: selDeal.id, facilityId: selDeal.facility_id, occurredAt: new Date().toISOString() } }),
            () => dealActs.reload(),
          ),
        onPrep: () => sales.setTab('Sales Kit', { kitMode: 'lib', slDoc: 'Product One-Pager', kitPzQuery: title(selDeal.facility_name ?? selDeal.name) }),
        onMp: () => selDeal.facility_id && sales.openPrep(selDeal.facility_id),
        meet: (selDeal.next_meeting_at ?? selMy?.next_meeting_at ?? '')?.slice(0, 10) ?? '',
        meetNote: selDeal.next_meeting_at ? soonOf(selDeal.next_meeting_at) || 'Booked' : 'No meeting booked',
        onMeetDate: (e: ChangeEvent<HTMLInputElement>) => {
          const val = e.target.value;
          if (!selOk) return;
          run(() => crmDealUpdate(client, { params: { id: selDeal.id }, body: { nextMeetingAt: val || null } }), () => dealActs.reload());
        },
        onLost: () => selOk && setLostOpen((o) => !o),
        note,
        onNote: (e: ChangeEvent<HTMLInputElement>) => setNote(e.target.value),
        onAddNote: () => {
          const t = note.trim();
          if (!t || !selOk) return;
          run(() => crmActivityCreate(client, { body: { type: 'note', subject: t, dealId: selDeal.id, facilityId: selDeal.facility_id } }), () => {
            setNote('');
            dealActs.reload();
          });
        },
        ghost: ghost + (selOk ? '' : disabledWrite),
        ghostRead: ghost,
        prim: primDeal,
        writeTitle: writeTitle(selDeal),
        id: selDeal.id,
        writable: selOk,
      }
    : { steps: [], timeline: [] };
  const lostFor = dropLost ? allDeals.find((d) => d.id === dropLost) ?? null : selDeal;
  const panel = {
    crmHasSel2: !!selDeal,
    crmNoSel2: !selDeal,
    crmStepGrid: 'display:grid;grid-template-columns:repeat(' + Math.max(1, selStages.length) + ',minmax(0,1fr));gap:4px;',
    crmD,
    crmLostOpen: lostOpen && !!selDeal,
    crmLostReasons: LOST_REASONS.map((r) => ({
      n: r,
      style: ghost,
      onClick: () => {
        const d = lostFor;
        if (!d || !mayWrite(d)) return;
        run(
          () => crmDealLost(client, { params: { id: d.id }, body: { lostReason: r } }),
          () => {
            setLostOpen(false);
            setDropLost(null);
            setParam('crmSel', null);
          },
        );
      },
    })),
    crmShowPanel: panelOn,
    crmPanelDeal: !isCat,
    crmPanelCat: isCat,
    crmNarrow: narrow,
    crmPanelClose: () => setPanelOpen(false),
    crmPanelStyle:
      'min-width:0;min-height:0;overflow-y:auto;display:flex;flex-direction:column;gap:10px;' +
      (narrow
        ? 'position:absolute;right:0;top:0;bottom:0;width:min(320px,80%);z-index:30;background:#F7F3EA;padding:10px 12px;border-radius:10px;box-shadow:-10px 0 30px rgba(34,39,46,.18);animation:riseIn .25s cubic-bezier(.22,.61,.36,1);'
        : 'border-left:1px solid rgba(34,39,46,.12);padding-left:12px;'),
    actErr,
  };

  // ── deals table (L21262-21274) ──
  // crm-extras: select (click the mark, Shift-click a range, Ctrl/Cmd+A, Esc), sort by a
  // heading, choose columns, filter by owner or tag, edit owner / value / next meeting in
  // the row, and saved deal views. The resting table is the design's, column for column.
  const dealVisible = allDeals
    .map((d) => (overrides[d.id] ? ({ ...d, ...overrides[d.id] } as CrmDeal) : d))
    .filter((d) => (view === 'Lost' ? d.status === 'lost' : view === 'Snoozed' ? !!d.snoozed_until && Date.parse(d.snoozed_until) > now : d.status !== 'lost'))
    .filter((d) => !q.trim() || title(d.name).toLowerCase().includes(q.trim().toLowerCase()))
    .filter((d) => !dOwnerF || (dOwnerF === 'me' ? d.owner_id === userId : dOwnerF === 'none' ? !d.owner_id : d.owner_id === dOwnerF))
    .filter((d) => !dTagF || (d.tags ?? []).some((t) => t.toLowerCase() === dTagF.toLowerCase()));
  const dealVal = (d: CrmDeal) => dealDollars(d, myDayById).v;
  const dealLast = (d: CrmDeal) => d.last_contact_at ?? myDayById.get(d.id)?.last_contact_at ?? null;
  const stageIdx = (d: CrmDeal) => stagesOf(d.pipeline_id).findIndex((s) => s.id === d.stage_id);
  const SORTERS: Record<DealCol, ((d: CrmDeal) => string | number | null) | null> = {
    facility: (d) => title(d.name).toLowerCase(),
    stage: stageIdx,
    owner: (d) => (d.owner_id ? firstName(memberList, d.owner_id).toLowerCase() : null),
    value: dealVal,
    last: (d) => { const l = dealLast(d); return l ? Date.parse(l) : null; },
    next: null,
    meet: (d) => (d.next_meeting_at ? Date.parse(d.next_meeting_at) : null),
    tags: (d) => (d.tags ?? []).join(',').toLowerCase() || null,
    close: (d) => (d.close_date ? Date.parse(d.close_date) : null),
  };
  const dealIds = dSort && SORTERS[dSort.by]
    ? dealVisible.slice().sort((a, b) => {
        const f = SORTERS[dSort.by]!;
        const x = f(a);
        const y = f(b);
        if (x == null && y == null) return 0;
        if (x == null) return 1; // blanks last, either way
        if (y == null) return -1;
        return (x < y ? -1 : x > y ? 1 : 0) * (dSort.dir === 'asc' ? 1 : -1);
      })
    : dealVisible;
  const cols = DEAL_COLS.filter((c) => c.key === 'facility' || dCols.includes(c.key));
  const dealGrid = 'grid-template-columns:' + cols.map((c) => c.w).join(' ') + ';';
  const dSelIds = dealIds.filter((d) => dSel[d.id]).map((d) => d.id);
  const allSel = dealIds.length > 0 && dSelIds.length === dealIds.length;
  const toggleSel = (i: number, e?: MouseEvent) => {
    const d = dealIds[i];
    if (!d) return;
    if (e?.shiftKey && anchor.current != null && dealIds[anchor.current]) {
      const [a, b] = anchor.current < i ? [anchor.current, i] : [i, anchor.current];
      const on = !dSel[d.id];
      setDSel((m) => {
        const n = { ...m };
        for (let j = a; j <= b; j++) n[dealIds[j].id] = on;
        return n;
      });
    } else {
      setDSel((m) => ({ ...m, [d.id]: !m[d.id] }));
    }
    anchor.current = i;
  };
  /** Inline edit: show the new value at once, roll it back if the server refuses. */
  const editDeal = (d: CrmDeal, patch: CrmDealPatch, shown: Partial<CrmDeal>) => {
    setGate(null);
    setOverrides((o) => ({ ...o, [d.id]: { ...(o[d.id] ?? {}), ...shown } }));
    const drop = () => setOverrides((o) => { const n = { ...o }; delete n[d.id]; return n; });
    crmDealUpdate(client, { params: { id: d.id }, body: { ...patch, expectedUpdatedAt: d.updated_at } })
      .then(() => { reloadDeals(); setTimeout(drop, 1500); })
      .catch((err: ApiError) => { drop(); setGate(errText(err)); });
  };
  const sortIcon = (c: DealCol) => (dSort?.by === c ? (dSort.dir === 'asc' ? ' ↑' : ' ↓') : '');
  const crmDealHead = cols.map((c) => ({
    n: c.n + sortIcon(c.key),
    key: c.key,
    onClick: SORTERS[c.key]
      ? () => setDSort(dSort?.by === c.key ? (dSort.dir === 'asc' ? { by: c.key, dir: 'desc' } : null) : { by: c.key, dir: c.key === 'facility' || c.key === 'owner' || c.key === 'stage' ? 'asc' : 'desc' })
      : null,
    ariaSort: dSort?.by === c.key ? (dSort.dir === 'asc' ? 'ascending' : 'descending') : undefined,
  }));
  const crmDealRows = dealIds.map((d, i) => {
    const my = myDayById.get(d.id);
    const last = d.last_contact_at ?? my?.last_contact_at ?? null;
    const lastKind = d.last_contact_kind ?? my?.last_contact_kind ?? null;
    const val = dealDollars(d, myDayById);
    const ok = mayWrite(d);
    const claimable = canWrite && !isManager && !d.owner_id && !!userId;
    return {
      id: d.id,
      cols: cols.map((c) => c.key),
      name: title(d.name),
      sel: !!dSel[d.id],
      selMark: dSel[d.id] ? '☑' : '☐',
      onMark: (e: MouseEvent) => { e.stopPropagation(); e.preventDefault(); toggleSel(i, e); },
      stage: d.stage_id,
      opts: stagesOf(d.pipeline_id).map((s) => ({ v: s.id, n: cap(s.name) })),
      owner: firstName(memberList, d.owner_id),
      ownerId: d.owner_id,
      ownerEditable: canWrite && (isManager || claimable),
      ownerClaimOnly: !isManager,
      onOwner: (to: string | null) => editDeal(d, { ownerId: to }, { owner_id: to }),
      value: val.v != null ? k(val.v) : '–',
      valueRaw: d.amount_cents != null ? String(Math.round(Number(d.amount_cents) / 100)) : '',
      onValue: (raw: string) => {
        const t = raw.trim();
        const n = t === '' ? null : Number(t);
        if (n != null && (!Number.isFinite(n) || n < 0)) { setGate('Value must be a dollar amount of 0 or more.'); return; }
        const cents = n == null ? null : Math.round(n * 100);
        editDeal(d, { amountCents: cents }, { amount_cents: cents, value: undefined });
      },
      last: last ? (lastKind ? cap(lastKind) : 'Contact') + ' · ' + ago(last, now) : 'None yet',
      lastStyle: 'font-size:14px;font-weight:600;white-space:nowrap;color:' + (last ? '#54606C' : '#B3261E') + ';',
      next: NEXT[stageKey(d.stage_name)] || '–',
      meet: d.next_meeting_at ? d.next_meeting_at.slice(0, 10) : '–',
      meetRaw: d.next_meeting_at ? d.next_meeting_at.slice(0, 10) : '',
      onMeet: (raw: string) => {
        const t = raw.trim();
        if (t && Number.isNaN(Date.parse(t))) { setGate('Next meeting must be a date.'); return; }
        const iso = t ? new Date(t + 'T15:00:00Z').toISOString() : null;
        editDeal(d, { nextMeetingAt: iso }, { next_meeting_at: iso });
      },
      tags: (d.tags ?? []).join(', ') || '–',
      close: d.close_date ? String(d.close_date).slice(0, 10) : '–',
      editable: ok,
      stop: (e: MouseEvent) => e.stopPropagation(),
      disabled: !ok,
      writeTitle: writeTitle(d),
      style:
        'display:grid;' + dealGrid + 'gap:10px;align-items:center;padding:6px 10px;border-bottom:1px solid rgba(34,39,46,.06);cursor:pointer;' +
        (dSel[d.id] ? 'background:rgba(79,138,214,.08);' : selDeal?.id === d.id ? 'background:rgba(79,138,214,.1);' : ''),
      onSel: (e?: MouseEvent) => {
        if (e && (e.shiftKey || e.metaKey || e.ctrlKey)) { e.preventDefault(); toggleSel(i, e); return; }
        selectDeal(d.id);
      },
      onStage: (e: ChangeEvent<HTMLSelectElement>) => {
        const to = stagesOf(d.pipeline_id).find((s) => s.id === e.target.value);
        if (!to || !ok) return;
        if (to.is_lost) {
          selectDeal(d.id);
          setLostOpen(true);
          return;
        }
        setGate(null);
        crmDealMove(client, { params: { id: d.id }, body: { stageId: to.id } })
          .then(() => reloadDeals())
          .catch((err: ApiError) => setGate(errText(err)));
      },
    };
  });
  // Bulk on deals: the stages offered are the first selected deal's track (a deal on the
  // other track is skipped by the server with the reason).
  const firstSel = dealIds.find((d) => dSel[d.id]) ?? null;
  const bulkStages = firstSel ? stagesOf(firstSel.pipeline_id) : [];
  const nDeals = (n: number) => n + (n === 1 ? ' deal' : ' deals');
  const dealBulkActs: Array<{ n: string; kind: BulkKind | 'claim'; show: boolean }> = [
    { n: 'Add tag', kind: 'tag_add', show: true },
    { n: 'Remove tag', kind: 'tag_remove', show: true },
    { n: 'Change owner', kind: 'owner', show: isManager },
    { n: 'Assign to me', kind: 'claim', show: !isManager && !!userId },
    { n: 'Move stage', kind: 'stage', show: true },
  ];
  const tagsSeen = [...new Set(allDeals.flatMap((d) => d.tags ?? []))].sort((a, b) => a.localeCompare(b));
  const dealViewNow: ViewState = { filters: { view, q: q.trim() || undefined, owner: dOwnerF || undefined, tag: dTagF || undefined }, sort: dSort, columns: dCols };
  const dealViewBase: ViewState = { filters: { view }, sort: null, columns: DEFAULT_DEAL_COLS };
  const activeDealView = dealViewList.find((x) => x.id === dealViewOn) ?? null;
  const applyDealView = (sv: CrmSavedView | null) => {
    const f = (sv?.filters ?? {}) as { view?: string; q?: string; owner?: string; tag?: string };
    const rv = !sv ? (DEAL_VIEWS.includes(view) ? view : 'All deals') : DEAL_VIEWS.includes(f.view as RailView) ? (f.view as RailView) : 'All deals';
    setParam('crmV', rv);
    setSavedOn(null);
    setQ(f.q ?? '');
    setDOwnerF(f.owner ?? '');
    setDTagF(f.tag ?? '');
    const s = sv?.sort as { by?: DealCol; dir?: 'asc' | 'desc' } | null | undefined;
    setDSort(s?.by && DEAL_COLS.some((c) => c.key === s.by) ? { by: s.by, dir: s.dir === 'asc' ? 'asc' : 'desc' } : null);
    setDCols(sv && Array.isArray(sv.columns) && sv.columns.length ? (sv.columns.filter((c) => DEAL_COLS.some((x) => x.key === c)) as DealCol[]) : DEFAULT_DEAL_COLS);
    setDSel({});
    setDealViewOn(sv?.id ?? null);
  };
  // A person's default deal view opens the first time the deals table shows.
  useEffect(() => {
    if (defaultApplied.current.deal || !isDealV || mode !== 'Table' || !dealViews.data) return;
    defaultApplied.current.deal = true;
    const def = dealViewList.find((x) => x.is_default);
    if (def && !dealViewOn) applyDealView(def);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDealV, mode, dealViews.data]);
  const dealsV = {
    crmVName: view,
    crmDealCount: crmDealRows.length + (crmDealRows.length === 1 ? ' deal' : ' deals'),
    // Restored from production: the deals as CSV (the server's export, the view's status).
    crmDealExport: () => run(() => downloadCsv(client, CRM_DEALS_EXPORT_PATH + (view === 'Lost' ? '?status=lost' : ''), 'aithora-deals.csv')),
    crmDealRows,
    crmDealHead,
    crmDealHeadStyle:
      'position:sticky;top:0;z-index:2;background:#F7F3EA;display:grid;' + dealGrid + 'gap:10px;padding:6px 10px;border-bottom:1px solid rgba(34,39,46,.14);font-size:14px;letter-spacing:.08em;text-transform:uppercase;color:#54606C;font-weight:700;',
    crmDealHasRows: crmDealRows.length > 0,
    crmDealSelAllMark: allSel ? '☑' : '☐',
    crmDealSelAll: () => setDSel(allSel ? {} : Object.fromEntries(dealIds.map((d) => [d.id, true]))),
    crmDealKeys: (e: KeyboardEvent<HTMLDivElement>) => {
      const t = e.target as HTMLElement;
      if (t.tagName === 'INPUT' || t.tagName === 'SELECT' || t.tagName === 'TEXTAREA') return;
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'a' && dealIds.length) {
        e.preventDefault();
        setDSel(Object.fromEntries(dealIds.map((d) => [d.id, true])));
      }
      if (e.key === 'Escape' && dSelIds.length) { e.preventDefault(); setDSel({}); setDealBulk(null); }
    },
    crmDealNone: !!deals.data && crmDealRows.length === 0,
    crmDealNoneNote:
      view === 'Lost' ? 'No lost deals. A deal lands here with its reason when it is marked lost.' : view === 'Snoozed' ? 'Nothing snoozed.' : 'No deals yet. Start one from Companies: search, tick facilities, Start deals.',
    crmGateHas: !!gate,
    crmGateMsg: gate ?? '',
    // Bulk (shown only with a selection).
    crmDealSelN: dSelIds.length,
    crmDealSelLine: nDeals(dSelIds.length) + ' selected' + (dSelIds.length > CRM_BULK_CAP ? ` (at most ${CRM_BULK_CAP} at once)` : ''),
    crmDealBulk: dealBulkActs.filter((a) => a.show).map((a) => ({
      n: a.n,
      on: dealBulk === a.kind,
      style: BTN + (canWrite ? '' : disabledWrite) + (dealBulk === a.kind ? 'border-color:#2F5C97;color:#2F5C97;' : ''),
      onClick: () => {
        if (!canWrite) return;
        if (a.kind === 'claim') {
          void bulkD.apply({ entity: 'deals', action: 'owner', ids: dSelIds, value: userId }, (r) => bulkLine(r, 'Assigned to you:', ['deal', 'deals']));
          return;
        }
        setDealBulk(dealBulk === a.kind ? null : a.kind);
      },
    })),
    crmDealBulkKind: dealBulk,
    crmDealBulkOptions:
      dealBulk === 'owner'
        ? [{ v: '', n: 'Unassigned' }, ...(memberList ?? []).map((m) => ({ v: m.id, n: m.name ?? m.email ?? 'A teammate' }))]
        : dealBulk === 'stage'
          ? bulkStages.map((s) => ({ v: s.id, n: cap(s.name) }))
          : undefined,
    crmDealBulkLostIds: bulkStages.filter((s) => s.is_lost).map((s) => s.id),
    crmDealBulkApply: (value: string | null, lostReason?: string) => {
      const kind = dealBulk;
      if (!kind) return;
      const what = kind === 'tag_add' ? `Tagged "${value}":` : kind === 'tag_remove' ? `Removed "${value}" from` : kind === 'owner' ? 'Reassigned' : 'Moved';
      void bulkD.apply({ entity: 'deals', action: kind, ids: dSelIds, value, lostReason: lostReason ?? null }, (r) => bulkLine(r, what, ['deal', 'deals'])).then((r) => {
        if (r) setDealBulk(null);
      });
    },
    crmDealBulkCancel: () => setDealBulk(null),
    crmDealClearSel: () => { setDSel({}); setDealBulk(null); },
    crmDealBulkBusy: bulkD.busy,
    crmDealToast: bulkD.toast,
    crmDealUndo: () => void bulkD.undo(),
    crmDealToastDismiss: bulkD.dismiss,
    // Columns, owner and tag filters, saved views (shown when there are deals or a view is open).
    crmDealTools: crmDealRows.length > 0 || allDeals.length > 0 || !!activeDealView,
    crmDealColsOpen: colsOpen,
    crmDealColsToggle: () => setColsOpen((o) => !o),
    crmDealColChoices: DEAL_COLS.filter((c) => c.key !== 'facility').map((c) => ({
      n: c.n,
      on: dCols.includes(c.key),
      onChange: () => setDCols((cs) => (cs.includes(c.key) ? cs.filter((x) => x !== c.key) : DEAL_COLS.map((x) => x.key).filter((x) => x === c.key || cs.includes(x)))),
    })),
    crmDealOwnerF: dOwnerF,
    crmDealOwnerOpts: [
      { v: '', n: 'Any owner' },
      ...(userId ? [{ v: 'me', n: 'Mine' }] : []),
      { v: 'none', n: 'Unassigned' },
      ...(memberList ?? []).filter((m) => m.id !== userId).map((m) => ({ v: m.id, n: m.name ?? m.email ?? 'A teammate' })),
    ],
    crmDealSetOwnerF: (e: ChangeEvent<HTMLSelectElement>) => { setDOwnerF(e.target.value); setDSel({}); },
    crmDealTagF: dTagF,
    crmDealTagOpts: [{ v: '', n: 'Any tag' }, ...tagsSeen.map((t) => ({ v: t, n: t }))],
    crmDealSetTagF: (e: ChangeEvent<HTMLSelectElement>) => { setDTagF(e.target.value); setDSel({}); },
    crmDealView: {
      object: 'deal' as const,
      active: activeDealView,
      current: dealViewNow,
      dirty: !sameView(dealViewNow, activeDealView ? { ...viewStateOf(activeDealView), columns: activeDealView.columns?.length ? activeDealView.columns : DEFAULT_DEAL_COLS } : dealViewBase),
      onSaved: (sv: CrmSavedView | null) => { dealViews.reload(); if (sv) setDealViewOn(sv.id); },
      onReset: () => applyDealView(activeDealView),
      onClose: () => { setDealViewOn(null); dealViews.reload(); },
    },
  };

  // ── board (crmStages, L21416-21460) ──
  const boardStages = (trackPipe?.stages ?? []).slice().sort((a, b) => a.sort - b.sort);
  const trackDeals = allDeals.filter((d) => d.pipeline_id === trackPipe?.id);
  const otherTrack = allDeals.filter((d) => d.pipeline_id !== trackPipe?.id && pipelineOf(d.pipeline_id)?.type === 'facility').length;
  const stageColor = (s: { name: string; is_won: boolean; is_lost: boolean }, i: number) =>
    s.is_lost ? '#B3261E' : s.is_won ? '#1F6B52' : i < 2 ? '#2F5C97' : /pilot|contract|corporate/i.test(s.name) ? '#264C7E' : '#5B6B7C';
  const moveTo = (dealId: string, stageId: string) => {
    const d = allDeals.find((x) => x.id === dealId);
    const to = boardStages.find((s) => s.id === stageId);
    if (!d || !to || d.stage_id === stageId) return;
    if (!mayWrite(d)) {
      setGate(writeTitle(d) ?? notYours);
      return;
    }
    if (to.is_lost) {
      setDropLost(d.id);
      setGate(null);
      return;
    }
    setGate(null);
    crmDealMove(client, { params: { id: d.id }, body: { stageId } })
      .then(() => reloadDeals())
      .catch((err: ApiError) => setGate(errText(err)));
  };
  const board = {
    crmTrackTabs: [
      ['A', 'Track A · single site'],
      ['B', 'Track B · multi-site'],
    ].map((x) => ({
      name: x[1],
      onClick: () => setParam('crmTrack', x[0] === 'A' ? null : x[0]),
      style:
        'display:inline-flex;align-items:center;height:28px;padding:0 12px;border-radius:7px;font-size:14px;font-weight:700;text-decoration:none;white-space:nowrap;' +
        (track === x[0] ? 'background:#2F5C97;color:#fff;' : 'background:#FCFAF4;color:#3C454F;border:1px solid rgba(34,39,46,.16);'),
    })),
    crmTrackScope:
      trackDeals.length + (trackDeals.length === 1 ? ' deal' : ' deals') + ' on this track' + (otherTrack ? ' · ' + otherTrack + ' on the other track, counted there' : ''),
    crmTrackNote:
      track === 'B'
        ? 'Eleven stages. InfoSec, legal and procurement are separate gates because a multi-site operator runs them separately, and so a deal parked in procurement is not a deal losing interest, and the two need different work.'
        : 'Six stages, one building. A single site can go lead to live without a corporate review, which is why it is a different pipeline rather than a shorter one.',
    crmStages: boardStages.map((s, xi) => {
      const mine = trackDeals.filter((d) => d.stage_id === s.id);
      const isOver = dragOver === s.id && !!dragging;
      return {
        name: stageKey(s.name),
        count: String(mine.length),
        empty: mine.length === 0,
        deals: mine.map((d) => ({
          name: d.name,
          meta: (xi === 0 ? 'started ' + ago(d.created_at, now) + ' · ' : 'moved here · ') + cap(s.name),
          draggable: mayWrite(d),
          drag: (ev: DragEvent<HTMLElement>) => {
            ev.dataTransfer?.setData('text/plain', d.id);
            if (ev.dataTransfer) ev.dataTransfer.effectAllowed = 'move';
            setDragging(d.id);
          },
          dragEnd: () => {
            setDragging(null);
            setDragOver(null);
          },
          cardStyle:
            'border:1px solid rgba(34,39,46,.12);border-radius:9px;background:#FBF8F1;padding:9px 11px;margin-bottom:7px;cursor:' +
            (mayWrite(d) ? 'grab' : 'default') +
            ';animation:riseIn .28s cubic-bezier(.22,.61,.36,1);' +
            (dragging === d.id ? 'opacity:.45;' : ''),
          title: mayWrite(d) ? undefined : writeTitle(d),
        })),
        onDragOver: (ev: DragEvent<HTMLElement>) => {
          ev.preventDefault();
          if (dragOver !== s.id) setDragOver(s.id);
        },
        onDragLeave: () => {
          if (dragOver === s.id) setDragOver(null);
        },
        onDrop: (ev: DragEvent<HTMLElement>) => {
          ev.preventDefault();
          const id = ev.dataTransfer?.getData('text/plain') || dragging;
          setDragging(null);
          setDragOver(null);
          if (id) moveTo(id, s.id);
        },
        colStyle:
          'animation:riseIn .3s cubic-bezier(.22,.61,.36,1);animation-delay:' +
          Math.min(300, xi * 30) +
          'ms;box-sizing:border-box;border-radius:11px;padding:11px 13px;min-width:0;' +
          (isOver ? 'background:rgba(79,138,214,.09);border:1px dashed rgba(47,92,151,.55);' : 'background:#FCFAF4;border:1px solid rgba(34,39,46,.12);'),
        dot: 'width:7px;height:7px;border-radius:50%;flex:0 0 7px;background:' + (STAGE_COLOR[stageKey(s.name)] ?? stageColor(s, xi)) + ';',
        countStyle: chip(mine.length ? '#264C7E' : '#54606C', mine.length ? 'rgba(79,138,214,.16)' : 'rgba(34,39,46,.08)'),
      };
    }),
    crmDragHint: gate ?? (trackDeals.length ? 'Drag a deal between stages to move it. Every move is logged with who and when.' : ''),
    crmHasDeals: trackDeals.length > 0 || !!gate,
    boardLostHas: !!dropLost,
    boardLostName: dropLost ? title(allDeals.find((d) => d.id === dropLost)?.name ?? '') : '',
    boardLostCancel: () => setDropLost(null),
    moveTo,
  };

  // ── catalog (c*, L21149-21341) ──
  const catRows: CatalogRow[] = [...(catalog.data?.rows ?? []), ...more];
  const catTotal = catalog.data?.total ?? 0;
  const catNext = more.length ? cursor : catalog.data?.next_cursor ?? null;
  const opRows: CrmOperatorRow[] = ops.data?.rows ?? [];
  const isOps = cKind === 'Operators';
  const cellS = 'font-size:16px;color:#22272E;font-variant-numeric:tabular-nums;overflow-wrap:break-word;';
  const groups = new Map((catalog.data?.groups ?? []).map((g) => [g.operator, g.count]));
  const disp: any[] = [];
  let lastOp: string | null = null;
  for (const r of catRows) {
    const op = r.parent_operator ?? 'Not on file';
    if (group && op !== lastOp) {
      lastOp = op;
      disp.push({ isHead: true, isRow: false, head: op + ' · ' + (groups.get(op) ?? catRows.filter((x) => (x.parent_operator ?? 'Not on file') === op).length) + ' in results' });
    }
    const d = r.deal && r.deal.status !== 'lost' ? cap(r.deal.stage) : '';
    const conn = r.connection ?? null;
    disp.push({
      isHead: false,
      isRow: true,
      name: title(r.name),
      loc: (r.city ? r.city + ', ' : '') + (r.state ?? '') + ' · ' + (r.parent_operator ?? 'Not on file'),
      selMark: cSel[r.id] ? '☑' : '☐',
      style:
        'display:grid;grid-template-columns:22px minmax(0,2.2fr) 58px 42px 46px 50px 40px 92px 72px 34px 70px 78px;gap:8px;align-items:center;padding:6px 10px;border-bottom:1px solid rgba(34,39,46,.06);cursor:pointer;' +
        (cPick === r.id ? 'background:rgba(79,138,214,.1);' : cSel[r.id] ? 'background:rgba(79,138,214,.05);' : ''),
      onPick: () => {
        setCPick(r.id);
        setPanelOpen(true);
      },
      onSel: (e: MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setCSel((m) => ({ ...m, [r.id]: !m[r.id] }));
      },
      cells: (
        [
          [r.type ?? '–'],
          [r.beds != null ? String(r.beds) : '–'],
          [r.overall_stars != null ? r.overall_stars + '★' : '–'],
          [r.staffing_stars != null ? r.staffing_stars + '★' : '–', r.staffing_stars != null && r.staffing_stars <= 2 ? '#B3261E' : null],
          [r.penalties.count != null ? String(r.penalties.count) : '–', (r.penalties.count ?? 0) >= 3 ? '#B3261E' : null],
          [r.ownership ?? 'Unknown'],
          [r.open_dollars ? k10(r.open_dollars.per_year) : '–'],
          [r.tier?.value ?? '–', r.tier?.value === 'A' ? '#12503C' : null],
          [d || '–', d ? '#264C7E' : '#54606C'],
          conn ? [conn.level === 'No connection' ? '–' : conn.level, /strong/i.test(conn.level) ? '#12503C' : conn.level === 'Good' ? '#264C7E' : '#54606C'] : ['–', '#54606C'],
        ] as Array<[string, string | null | undefined]>
      ).map((x) => ({ v: x[0], s: cellS + (x[1] ? 'color:' + x[1] + ';font-weight:700;' : '') })),
    });
  }
  const opGrid = 'display:grid;grid-template-columns:minmax(0,2fr) 84px 46px minmax(0,1fr) 56px 46px 46px 84px 56px;gap:8px;align-items:center;padding:6px 10px;border-bottom:1px solid rgba(34,39,46,.06);cursor:pointer;';
  const opKind = (kd: string) => ({ pe_owner: 'PE-backed', chain: 'Chain', nonprofit: 'Nonprofit', government: 'Government', operator: 'Operator', mgmt_co: 'Management co.' })[kd] ?? cap(kd);
  const opDisp = opRows.map((o) => ({
    isHead: false,
    isRow: true,
    name: o.name,
    loc: o.largest ? 'Largest: ' + title(o.largest.name) + ' · ' + (o.largest.state ?? '') : o.sites + (o.sites === 1 ? ' site' : ' sites'),
    selMark: '',
    onSel: (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
    },
    style: opGrid.replace('grid-template-columns:', 'grid-template-columns:0 ') + (cPickOp === o.id ? 'background:rgba(79,138,214,.1);' : ''),
    onPick: () => {
      setCPickOp(o.id);
      setPanelOpen(true);
    },
    cells: (
      [
        [opKind(o.kind)],
        [String(o.sites)],
        [o.states.slice().sort().join(', ')],
        [o.beds != null ? o.beds.toLocaleString('en-US') : '–'],
        [o.avg_staffing_stars != null ? o.avg_staffing_stars.toFixed(1) + '★' : '–', o.avg_staffing_stars != null && o.avg_staffing_stars <= 2.2 ? '#B3261E' : null],
        [o.penalties != null ? String(o.penalties) : '–', (o.penalties ?? 0) >= 10 ? '#B3261E' : null],
        [o.open_dollars ? '$' + (o.open_dollars.per_year >= 1e6 ? (o.open_dollars.per_year / 1e6).toFixed(2) + 'M' : Math.round(o.open_dollars.per_year / 1000) + 'k') : '–'],
        [o.open_deals ? String(o.open_deals) : '–', o.open_deals ? '#264C7E' : '#54606C'],
      ] as Array<[string, string | null | undefined]>
    ).map((x) => ({ v: x[0], s: cellS + (x[1] ? 'color:' + x[1] + ';font-weight:700;' : '') })),
  }));
  const LBL: Record<string, (val: any) => string> = {
    state: (x: string[]) => x.join(', '),
    type: (x: string[]) => x.join(', '),
    ownership: (x: string[]) => x.map((o) => OWN_LABEL[o] ?? o).join(', '),
    beds_min: (x) => x + '+ beds',
    beds_max: (x) => 'under ' + x + ' beds',
    penalties_min: (x) => x + '+ penalties',
    has_penalties: () => 'has penalties',
    staffing_stars_max: (x) => 'staffing ≤ ' + x + '★',
    stars_max: (x) => 'overall ≤ ' + x + '★',
    stars_min: (x) => 'overall ≥ ' + x + '★',
    no_deal: () => 'no deal yet',
    grade: (x: string[]) => 'Grade ' + x.join(', '),
    ehr: (x) => x,
    operator: (x) => 'Operator: ' + x,
    operator_company_id: () => 'Operator: ' + (opRows.find((o) => o.id === cf.operator_company_id)?.name ?? 'selected'),
    q: (x) => '“' + x + '”',
    city: (x) => x,
    zip: (x) => x,
    score_min: (x) => 'score ≥ ' + x,
    deal_stage: (x) => 'stage ' + x,
    deal_owner: (x) => (x === 'me' ? 'my deals' : 'owner'),
  };
  const uniqOf = (vals: Array<string | null | undefined>, cur?: string) => [...new Set([...vals.filter((x): x is string => !!x), ...(cur ? [cur] : [])])].sort();
  type Facet = { n: string; val: string; opts: Array<{ v: string; n: string }>; onChange: (e: ChangeEvent<HTMLSelectElement>) => void; disabled?: boolean; title?: string };
  const facet = (nm: string, key: keyof CatalogFilters, list: Array<[string, string]>, kind: 'list' | 'num' | 'str' | 'bool'): Facet => {
    const cur = cf[key] as unknown;
    const val = cur == null ? '' : Array.isArray(cur) ? String(cur[0] ?? '') : String(cur);
    return {
      n: nm,
      val,
      opts: [{ v: '', n: 'Any' }, ...list.map((x) => ({ v: x[0], n: x[1] }))],
      onChange: (e) => {
        const v0 = e.target.value;
        const o: CatalogFilters = { ...cf };
        if (v0 === '') delete o[key];
        else if (kind === 'list') (o as any)[key] = [v0];
        else if (kind === 'num') (o as any)[key] = Number(v0);
        else if (kind === 'bool') (o as any)[key] = true;
        else (o as any)[key] = v0;
        setCf(o);
      },
    };
  };
  const catalogSel = Object.keys(cSel).filter((id) => cSel[id]);
  const pickRow = cPick ? catRows.find((r) => r.id === cPick) ?? null : null;
  const pickOp = cPickOp ? opRows.find((o) => o.id === cPickOp) ?? null : null;
  const parseInto = async () => {
    const text = q.trim();
    if (!text) return;
    try {
      const r = await crmFacilityParse(client, { body: { text } });
      setCf({ ...cf, ...r.filters });
      setQ('');
      setGate(r.unsupported.length ? 'Not applied: ' + r.unsupported.map((u) => u.text + ' (' + u.reason + ')').join('; ') : null);
    } catch (e) {
      setGate(errText(e));
    }
  };
  const HEAD: Array<[string, typeof sortBy.by | null]> = [
    ['Facility', 'name'],
    ['Type', null],
    ['Beds', 'beds'],
    ['★', 'overall_stars'],
    ['Staff', 'staffing_stars'],
    ['Pen.', 'penalties'],
    ['Owner', null],
    ['Open $', 'opportunity_score'],
    ['Tier', null],
    ['Deal', null],
    ['Conn.', null],
  ];
  const OPHEAD = ['Operator / owner', 'Kind', 'Sites', 'States', 'Beds', 'Staff', 'Pen.', 'Open $', 'Deals'];
  const OPSORT: OperatorSortKey[] = ['name', 'kind', 'sites', 'states', 'beds', 'staffing', 'penalties', 'open_dollars', 'deals'];
  const DEFAULT_CAT_SORT = { by: 'opportunity_score', dir: 'desc' } as const;
  const companyViews = (views.data?.views ?? []).filter((x) => (x.object ?? 'company') === 'company');
  const activeCompanyView = companyViews.find((x) => x.id === savedOn) ?? null;
  const companyViewNow: ViewState = { filters: cf as Record<string, unknown>, sort: sortBy, columns: [] };
  const applyCompanyView = (sv: CrmSavedView) => {
    setParam('crmV', 'Companies');
    setCf({ ...(sv.filters as CatalogFilters) });
    const so = sv.sort as typeof sortBy | null | undefined;
    setSortBy(so && typeof so === 'object' && so.by ? { by: so.by, dir: so.dir === 'asc' ? 'asc' : 'desc' } : { ...DEFAULT_CAT_SORT });
    setSavedOn(sv.id);
    setDealViewOn(null);
  };
  // A person's default company view opens the first time the catalog shows.
  useEffect(() => {
    if (defaultApplied.current.company || !isCat || !views.data) return;
    defaultApplied.current.company = true;
    const def = companyViews.find((x) => x.is_default);
    if (def && !savedOn && Object.keys(cf).length === 0) applyCompanyView(def);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCat, views.data]);
  const catalogV = {
    crmIsCatalog: isCat,
    cChips: Object.keys(cf).map((key) => ({
      n: (LBL[key] ?? String)((cf as any)[key]),
      onClick: () => {
        const o: CatalogFilters = { ...cf };
        delete (o as any)[key];
        setCf(o);
      },
    })),
    cHasChips: Object.keys(cf).length > 0,
    cClear: () => {
      setCf({});
    },
    cToggleF: () => setFOpen((o) => !o),
    cFOpen: fOpen,
    cFacets: [
      facet('State', 'state', uniqOf(facets.data?.state ?? catRows.map((r) => r.state), cf.state?.[0]).map((x) => [x, x]), 'list'),
      facet('Type', 'type', uniqOf(facets.data?.type ?? catRows.map((r) => r.type), cf.type?.[0]).map((x) => [x, x]), 'list'),
      facet('Ownership', 'ownership', Object.entries(OWN_LABEL), 'list'),
      facet('Beds', 'beds_min', [['50', '50+'], ['100', '100+'], ['150', '150+'], ['200', '200+']], 'num'),
      facet('Staffing', 'staffing_stars_max', [['1', '≤ 1★'], ['2', '≤ 2★'], ['3', '≤ 3★']], 'num'),
      facet('Penalties', 'penalties_min', [['1', '1+'], ['2', '2+'], ['3', '3+'], ['5', '5+']], 'num'),
      {
        n: 'Tier',
        val: '',
        opts: [{ v: '', n: 'Any' }, { v: 'A', n: 'A' }, { v: 'B', n: 'B' }, { v: 'C', n: 'C' }],
        onChange: () => setGate('Tier is computed per facility by the pilot-priority engine and is not stored on the catalog yet, so the server cannot filter by it.'),
      },
      facet('EHR', 'ehr', uniqOf([...(facets.data?.ehr ?? catRows.map((r) => r.ehr)), 'Unknown'], cf.ehr).map((x) => [x === 'Unknown' ? 'unknown' : x, x]), 'str'),
      facet('Deal', 'no_deal', [['true', 'No deal yet']], 'bool'),
      { n: 'Group', val: group ? 'op' : '', opts: [{ v: '', n: 'None' }, { v: 'op', n: 'By operator' }], onChange: (e: ChangeEvent<HTMLSelectElement>) => setGroup(e.target.value === 'op') },
    ],
    cSaveName: saveName,
    cSetSaveName: (e: ChangeEvent<HTMLInputElement>) => setSaveName(e.target.value),
    cSave: () => {
      const nm = saveName.trim();
      if (!nm || !canWrite) return;
      run(() => crmViewCreate(client, { body: { object: 'company', name: nm, filters: cf as Record<string, unknown>, scope: 'Mine', sort: sortBy } }), () => {
        setSaveName('');
        views.reload();
      });
    },
    cImportRef,
    cCanImport: canWrite,
    cImport: () => canWrite && cImportRef.current?.click(),
    cImportNote,
    cOnImportFile: async (e: ChangeEvent<HTMLInputElement>) => {
      const f = e.target.files?.[0];
      e.target.value = '';
      if (!f || !canWrite) return;
      const text = await f.text();
      run(() => crmCompaniesImport(client, { body: { csv: text } }).then((r) => {
        setCImportNote(`Imported: ${r.inserted ?? 0} added, ${r.updated ?? 0} updated, ${r.skipped ?? 0} skipped.`);
      }), () => catalog.reload());
    },
    cCountLine: isOps
      ? (ops.data ? ops.data.total.toLocaleString('en-US') : '–') + ' operators and owners across these results · click one to see its facilities'
      : catalog.data
        ? catTotal.toLocaleString('en-US') + ' match · live search covers all ' + (c ? plusK(c.catalog) : 'the catalog')
        : 'Searching…',
    cHead: isOps
      ? OPHEAD.map((h, i) => {
          const by = OPSORT[i];
          return {
            n: h + (opSort?.by === by ? (opSort.dir === 'asc' ? ' ↑' : ' ↓') : ''),
            onClick: () => setOpSort({ by, dir: opSort?.by === by ? (opSort.dir === 'asc' ? 'desc' : 'asc') : by === 'name' || by === 'kind' ? 'asc' : 'desc' }),
          };
        })
      : HEAD.map((h) => ({
          n: h[0] + (h[1] && sortBy.by === h[1] ? (sortBy.dir === 'asc' ? ' ↑' : ' ↓') : ''),
          onClick: () => {
            if (!h[1]) return;
            const by = h[1];
            setSortBy({ by, dir: sortBy.by === by ? (sortBy.dir === 'asc' ? 'desc' : 'asc') : by === 'name' ? 'asc' : 'desc' });
          },
        })),
    cRows: isOps ? opDisp : disp,
    cNone: isOps ? !!ops.data && opDisp.length === 0 : !!catalog.data && catTotal === 0,
    cKinds: (['Facilities', 'Operators'] as const).map((kd) => ({
      n: kd === 'Operators' ? 'Operators & owners' : 'Facilities',
      c: kd === 'Operators' ? (ops.data ? String(ops.data.total) : '–') : catalog.data ? String(catTotal) : '–',
      onClick: () => setCKind(kd),
      style:
        'display:inline-flex;align-items:center;gap:5px;height:26px;padding:0 10px;border-radius:6px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;' +
        (cKind === kd ? 'background:#2F5C97;color:#fff;' : 'color:#3C454F;background:rgba(34,39,46,.06);'),
    })),
    cHeadStyle:
      'position:sticky;top:0;z-index:2;background:#F7F3EA;display:grid;gap:8px;padding:6px 10px;border-bottom:1px solid rgba(34,39,46,.14);grid-template-columns:' +
      (isOps ? '0 minmax(0,2fr) 84px 46px minmax(0,1fr) 56px 46px 46px 84px 56px' : '22px minmax(0,2.2fr) 58px 42px 46px 50px 40px 92px 72px 34px 70px 78px') +
      ';',
    cSelAllMark: catRows.length && catRows.every((r) => cSel[r.id]) ? '☑' : '☐',
    cSelAll: () => {
      const all = catRows.length > 0 && catRows.every((r) => cSel[r.id]);
      const m: Record<string, boolean> = {};
      if (!all) catRows.forEach((r) => (m[r.id] = true));
      setCSel(m);
    },
    cHasSel: catalogSel.length > 0,
    cSelLine: catalogSel.length + ' selected',
    cBulk: (
      [
        ['Start deals', () => canWrite && run(() => crmBulkStart(client, { body: { facilityIds: catalogSel, track, source: 'catalog' } }), () => {
          setCSel({});
          catalog.reload();
        })],
        [
          'Assign to me',
          () => {
            if (!canWrite || !userId) return;
            const ids = catRows.filter((r) => cSel[r.id] && r.deal && r.deal.status === 'open').map((r) => r.deal!.id);
            run(() => Promise.all(ids.map((id) => crmDealReassign(client, { params: { id }, body: { ownerId: userId } }))), () => catalog.reload());
          },
        ],
        ['Show on map', () => sales.setTab('Targeting', catalogSel[0] ? { tgSel: catalogSel[0] } : {})],
        ['Export CSV', () => run(() => downloadCsv(client, CRM_FACILITY_EXPORT_PATH, 'aithora-facilities.csv', { method: 'POST', body: { facilityIds: catalogSel } }))],
      ] as Array<[string, () => void]>
    ).map((b) => ({ n: b[0], onClick: b[1] })),
    cClearSel: () => { setCSel({}); setCoBulk(null); },
    // crm-extras: tag, status and owner on the selected companies, with Undo.
    cBulkMore: (
      [
        ['Add tag', 'tag_add', true],
        ['Remove tag', 'tag_remove', true],
        ['Set status', 'status', true],
        ['Company owner', 'owner', isManager],
      ] as Array<[string, BulkKind, boolean]>
    )
      .filter((b) => b[2])
      .map((b) => ({
        n: b[0],
        style: BTN + (canWrite ? '' : disabledWrite) + (coBulk === b[1] ? 'border-color:#2F5C97;color:#2F5C97;' : ''),
        onClick: () => canWrite && setCoBulk(coBulk === b[1] ? null : b[1]),
      })),
    cBulkKind: coBulk,
    cBulkOptions:
      coBulk === 'status'
        ? CRM_COMPANY_STATUSES.map((x) => ({ v: x, n: cap(x) }))
        : coBulk === 'owner'
          ? [{ v: '', n: 'Unassigned' }, ...(memberList ?? []).map((m) => ({ v: m.id, n: m.name ?? m.email ?? 'A teammate' }))]
          : undefined,
    cBulkApply: (value: string | null) => {
      const kind = coBulk;
      if (!kind) return;
      const what = kind === 'tag_add' ? `Tagged "${value}":` : kind === 'tag_remove' ? `Removed "${value}" from` : kind === 'status' ? `Set to ${cap(String(value))}:` : 'Reassigned';
      void bulkC.apply({ entity: 'companies', action: kind, ids: catalogSel, value }, (r) => bulkLine(r, what, ['company', 'companies'])).then((r) => {
        if (r) setCoBulk(null);
      });
    },
    cBulkCancel: () => setCoBulk(null),
    cBulkBusy: bulkC.busy,
    cToast: bulkC.toast,
    cUndo: () => void bulkC.undo(),
    cToastDismiss: bulkC.dismiss,
    cView: {
      object: 'company' as const,
      active: activeCompanyView,
      current: companyViewNow,
      dirty: !!activeCompanyView && !sameView(companyViewNow, { filters: activeCompanyView.filters ?? {}, sort: activeCompanyView.sort ?? DEFAULT_CAT_SORT, columns: [] }),
      onSaved: (sv: CrmSavedView | null) => { views.reload(); if (sv) setSavedOn(sv.id); },
      onReset: () => activeCompanyView && applyCompanyView(activeCompanyView),
      onClose: () => { setSavedOn(null); views.reload(); },
    },
    cMore: !!catNext,
    cMoreLabel: 'Load ' + Math.min(CATALOG_PAGE, Math.max(0, catTotal - catRows.length)) + ' more · ' + Math.max(0, catTotal - catRows.length).toLocaleString('en-US') + ' left',
    cLoadMore: async () => {
      if (!catNext) return;
      try {
        const r = await crmFacilitySearch(client, { body: { filters: cf, sort: group ? { by: 'operator', dir: 'asc' } : sortBy, cursor: catNext, limit: CATALOG_PAGE } });
        setMore((m) => [...m, ...r.rows]);
        setCursor(r.next_cursor);
      } catch (e) {
        setGate(errText(e));
      }
    },
    cPickNone: isOps ? !pickOp : !pickRow,
    cPickHas: isOps ? !!pickOp : !!pickRow,
    cP:
      isOps && pickOp
        ? {
            name: pickOp.name,
            loc: opKind(pickOp.kind) + ' · ' + pickOp.sites + ' sites · ' + pickOp.states.slice().sort().join(', '),
            open: pickOp.open_dollars ? '$' + (pickOp.open_dollars.per_year >= 1e6 ? (pickOp.open_dollars.per_year / 1e6).toFixed(2) + 'M' : Math.round(pickOp.open_dollars.per_year / 1000) + 'k') + '/yr open' : 'Open dollars not modeled',
            modeled: !!pickOp.open_dollars,
            tier: pickOp.open_deals + (pickOp.open_deals === 1 ? ' open deal' : ' open deals'),
            facts: [
              ['Kind', opKind(pickOp.kind)],
              ['Sites', String(pickOp.sites)],
              ['Beds', pickOp.beds != null ? pickOp.beds.toLocaleString('en-US') : '–'],
              ['Avg overall', pickOp.avg_overall_stars != null ? pickOp.avg_overall_stars.toFixed(1) + '★' : '–'],
              ['Avg staffing', pickOp.avg_staffing_stars != null ? pickOp.avg_staffing_stars.toFixed(1) + '★' : '–'],
              ['Penalties', pickOp.penalties != null ? pickOp.penalties + ' recent, all sites' : '–'],
              ['Largest site', pickOp.largest ? title(pickOp.largest.name) + (pickOp.largest.open_dollars != null ? ' · ' + k(pickOp.largest.open_dollars) : '') : '–'],
            ].map((f) => ({ k: f[0], v: f[1] })),
            dealLabel: 'Show its ' + pickOp.sites + ' facilities',
            primary: prim,
            ghost,
            onDeal: () => {
              setCKind('Facilities');
              setCf({ ...cf, operator_company_id: pickOp.id });
              setCPickOp(null);
            },
            onMp: () => pickOp.largest && sales.openPrep(pickOp.largest.facility_id),
            onMap: () => sales.setTab('Targeting'),
            src: 'One record per operator or owner. Its facilities, people and deals link to it; open dollars are the sum of its sites, MODELED.',
          }
        : pickRow
          ? {
              name: title(pickRow.name),
              loc: (pickRow.city ? pickRow.city + ', ' : '') + (pickRow.state ?? '') + ' · ' + (pickRow.type ?? '–') + ' · ' + (pickRow.beds ?? '–') + ' beds',
              open: pickRow.open_dollars ? k10(pickRow.open_dollars.per_year) + '/yr open' : 'Open dollars not modeled',
              modeled: !!pickRow.open_dollars?.modeled,
              tier: pickRow.tier ? 'Tier ' + pickRow.tier.value : 'Tier not scored',
              facts: [
                ['Connection', pickRow.connection ? pickRow.connection.level + (pickRow.connection.strongest ? ' · ' + pickRow.connection.strongest : '') : done ? 'Not measured yet' : 'Connect email to score'],
                ['Operator', pickRow.parent_operator ?? 'Not on file'],
                ['Ownership', pickRow.ownership ?? 'Unknown'],
                ['Overall', pickRow.overall_stars != null ? pickRow.overall_stars + '★' : '–'],
                ['Staffing', pickRow.staffing_stars != null ? pickRow.staffing_stars + '★' : '–'],
                ['Penalties', pickRow.penalties.count != null ? pickRow.penalties.count + ' recent' : '–'],
                ['Margin', pickRow.operating_margin == null ? 'Not known · ask' : pickRow.operating_margin + '%'],
                ['EHR', pickRow.ehr ?? 'Not known · ask'],
              ].map((f) => ({ k: f[0], v: f[1] })),
              dealLabel: pickRow.deal && pickRow.deal.status !== 'lost' ? 'Open deal · ' + cap(pickRow.deal.stage) : '+ Start deal',
              primary: prim + (pickRow.deal || canWrite ? '' : disabledWrite),
              ghost,
              onDeal: () => {
                if (pickRow.deal && pickRow.deal.status !== 'lost') {
                  setParam('crmV', null);
                  setParam('crmSel', pickRow.deal.id);
                  return;
                }
                if (canWrite) run(() => crmBulkStart(client, { body: { facilityIds: [pickRow.id], track, source: 'catalog' } }), () => catalog.reload());
              },
              onMp: () => sales.openPrep(pickRow.id),
              onMap: () => sales.setTab('Targeting', { tgSel: pickRow.id }),
              src: 'CMS public data' + (pickRow.cms_as_of ? ' as of ' + pickRow.cms_as_of : '') + ' · open dollars MODELED.',
            }
          : { facts: [] },
  };

  // ── People (crmIsTable, contacts; L21461-21674) ──
  const contacts: CrmContact[] = people.data?.contacts ?? [];
  const pTotal = people.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(pTotal / PEOPLE_PAGE));
  const pageNo = Math.min(page, pageCount);
  const pSelIds = Object.keys(pSel).filter((x) => pSel[x]);
  const importRef = useRef<HTMLInputElement | null>(null);
  const ROLES = ['admin', 'owner', 'corporate', 'clinical_champion', 'economic_buyer', 'other'];
  const peopleV = {
    crmQuery: pq,
    crmSearch: (e: ChangeEvent<HTMLInputElement>) => {
      setPq(e.target.value);
      setPage(1);
    },
    crmSearchPh: 'Search name, email, phone, title…',
    crmFilters: [
      {
        name: 'Role: ' + (role ? role.replace(/_/g, ' ') : 'any'),
        style: btn(false),
        onClick: () => {
          const i = role ? ROLES.indexOf(role) : -1;
          setRole(i + 1 < ROLES.length ? ROLES[i + 1] : null);
          setPage(1);
        },
      },
    ],
    crmImport: () => canWrite && importRef.current?.click(),
    importRef,
    onImportFile: async (e: ChangeEvent<HTMLInputElement>) => {
      const f = e.target.files?.[0];
      e.target.value = '';
      if (!f) return;
      const text = await f.text();
      run(() => crmContactsImport(client, { body: { csv: text } }), () => people.reload());
    },
    crmExport: () => run(() => downloadCsv(client, CRM_CONTACTS_EXPORT_PATH, 'aithora-contacts.csv'), () => setExported(true)),
    crmExportLabel: exported ? 'Exported' : 'Export',
    crmCreate: () => {
      if (!canWrite) return;
      setContactId('new');
      setCtEdit(true);
      setCtFields({});
    },
    crmCreateLabel: '+ Contact',
    crmGhostStyle: btn(false) + 'height:30px;padding:0 10px;flex:0 0 auto;' + (canWrite ? '' : disabledWrite),
    crmPrimaryStyle: btn(true) + 'height:30px;padding:0 10px;flex:0 0 auto;' + (canWrite ? '' : disabledWrite),
    crmExportStyle: btn(false) + 'height:30px;padding:0 10px;flex:0 0 auto;',
    crmHeadGrid: 'display:grid;grid-template-columns:20px minmax(0,2fr) 120px minmax(0,1fr) 70px;gap:10px;padding:8px 14px;border-bottom:1px solid rgba(34,39,46,.12);',
    crmCols: [[''], ['Name'], ['Phone'], ['Title'], ['Role', 'right']].map((x) => ({ name: x[0], style: capt + (x[1] ? 'text-align:' + x[1] + ';' : '') })),
    crmHasViews: false,
    crmViews: [],
    crmViewNote: '',
    crmHasSel: pSelIds.length > 0,
    crmSelCount: pSelIds.length + ' selected',
    crmBulk: [
      { name: 'Rescore', onClick: () => setBulkNote('Rescoring contacts is not built on the server yet.'), style: btn(true) + 'height:28px;' + disabledWrite },
      { name: 'Add to targeting', onClick: () => setBulkNote('Adding contacts to Targeting is not built on the server yet.'), style: btn(false) + 'height:28px;' + disabledWrite },
      // Restored from production (crm_view.dart bulk contact reassign and archive).
      {
        name: 'Assign to me',
        onClick: () => {
          if (!canWrite || !userId) return;
          run(() => crmBulkReassign(client, { body: { entity: 'contacts', ids: pSelIds, ownerId: userId } }), () => { setBulkNote(pSelIds.length + ' assigned to you.'); people.reload(); });
        },
        style: btn(false) + 'height:28px;' + (canWrite ? '' : disabledWrite),
      },
      {
        name: 'Archive',
        onClick: () => {
          if (!canWrite) return;
          const ids = [...pSelIds];
          run(() => Promise.all(ids.map((id) => crmContactArchive(client, { params: { id }, body: {} }))), () => {
            setArchived(ids);
            setPSel({});
            setBulkNote(ids.length + ' archived. They are kept, hidden from lists; Restore brings them back.');
            people.reload();
          });
        },
        style: btn(false) + 'height:28px;' + (canWrite ? '' : disabledWrite),
      },
      {
        name: 'Export',
        onClick: () => {
          const rows = contacts.filter((x) => pSel[x.id]).map((x) => [x.name, x.email ?? '', x.phone ?? '', x.title ?? '', x.role ?? '']);
          exportCsv(['Name', 'Email', 'Phone', 'Title', 'Role'], rows, 'aithora-contacts-selected.csv');
        },
        style: btn(false) + 'height:28px;',
      },
    ],
    crmBulkNote: bulkNote ?? 'These act on the selected records. Every change is logged with who and when.',
    crmArchivedN: archived.length,
    crmRestoreArchived: () => {
      if (!canWrite || !archived.length) return;
      const ids = [...archived];
      run(() => Promise.all(ids.map((id) => crmContactRestore(client, { params: { id }, body: {} }))), () => {
        setArchived([]);
        setBulkNote(ids.length + ' restored.');
        people.reload();
      });
    },
    crmClearSel: () => setPSel({}),
    crmRows: contacts.map((x) => ({
      wrap:
        'display:grid;grid-template-columns:20px minmax(0,2fr) 120px minmax(0,1fr) 70px;gap:10px;align-items:center;padding:9px 14px;border-bottom:1px solid rgba(34,39,46,.06);text-decoration:none;color:inherit;cursor:pointer;' +
        (pSel[x.id] ? 'background:rgba(79,138,214,.06);' : ''),
      onClick: () => {
        setContactId(x.id);
        setCtEdit(false);
        setCtConfirm(false);
      },
      onSel: (e: MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setPSel((m) => ({ ...m, [x.id]: !m[x.id] }));
      },
      selMark: pSel[x.id] ? '✓' : '',
      selStyle:
        'display:inline-flex;align-items:center;justify-content:center;width:16px;height:16px;flex:0 0 16px;border-radius:4px;font-size:14px;font-weight:700;text-decoration:none;white-space:nowrap;' +
        (pSel[x.id] ? 'background:#2F5C97;color:#fff;' : 'background:#FCFAF4;border:1px solid rgba(34,39,46,.24);color:transparent;'),
      cells: [
        { v: x.name, style: cell('', 600) },
        { v: x.phone ?? '–', style: cell('', 400, '#54606C') + MONO },
        { v: x.title ?? x.kind ?? '–', style: cell('', 400, '#54606C') },
        { v: x.role ? x.role.replace(/_/g, ' ') : '–', style: chip('#54606C', 'rgba(34,39,46,.08)') + 'text-align:center;' },
      ],
    })),
    crmEmpty: !!people.data && contacts.length === 0,
    crmEmptyNote: pq.trim() ? 'Nothing matches "' + pq.trim() + '".' : 'No contacts yet.',
    crmPageLabel: pTotal ? (pageNo - 1) * PEOPLE_PAGE + 1 + '–' + Math.min(pageNo * PEOPLE_PAGE, pTotal) + ' of ' + pTotal.toLocaleString('en-US') : 'no rows',
    crmPageOf: 'page ' + pageNo + ' of ' + pageCount.toLocaleString('en-US'),
    crmHasPrev: pageNo > 1,
    crmHasNext: pageNo < pageCount,
    crmPrev: () => pageNo > 1 && setPage(pageNo - 1),
    crmNext: () => pageNo < pageCount && setPage(pageNo + 1),
    crmPrevStyle:
      'display:inline-flex;align-items:center;height:28px;padding:0 12px;border-radius:7px;font-size:14px;text-decoration:none;white-space:nowrap;' +
      (pageNo > 1 ? 'background:#FCFAF4;color:#3C454F;border:1px solid rgba(34,39,46,.16);' : 'background:rgba(34,39,46,.04);color:#B4BAC2;border:1px solid rgba(34,39,46,.08);'),
    crmNextStyle:
      'display:inline-flex;align-items:center;height:28px;padding:0 12px;border-radius:7px;font-size:14px;text-decoration:none;white-space:nowrap;' +
      (pageNo < pageCount ? 'background:#FCFAF4;color:#3C454F;border:1px solid rgba(34,39,46,.16);' : 'background:rgba(34,39,46,.04);color:#B4BAC2;border:1px solid rgba(34,39,46,.08);'),
    crmFoot:
      'Almost every row is a facility main line rather than a named person, which is the gap between a catalog and a contact list. The table holds ' +
      pTotal.toLocaleString('en-US') +
      ' of ' +
      (c ? c.contacts.toLocaleString('en-US') : '–') +
      ' tracked contacts.',
  };

  // ── Customers / Health panels (L20414-20429, crmIsPanel) ──
  const hc = health.data;
  const firstPilot = allDeals.filter((d) => d.pilot_started_at).sort((a, b) => String(a.pilot_started_at).localeCompare(String(b.pilot_started_at)))[0];
  const customers = c?.customers ?? 0;
  const PANELS: Record<'Customers' | 'Health', [string, string, Array<[string, string, string, string]>, Array<[string, string, string]>]> = {
    Customers: [
      'Live customers',
      customers
        ? customers + (customers === 1 ? ' facility is' : ' facilities are') + ' live on a paid subscription.'
        : 'No facility has converted from pilot yet, so this is empty by fact rather than by omission. It fills the moment the first Founding Facility signs.',
      [
        ['Live facilities', c ? String(customers) : '–', 'converted from pilot', customers ? '#1F6B52' : '#54606C'],
        ['Beds under contract', '–', 'per-bed subscription', '#54606C'],
        ['MRR', '–', 'from customers', '#54606C'],
        ['Founding partners', c ? String(customers) : '–', 'of ' + FOUNDING_SLOTS, '#1F6B52'],
      ],
      [
        ['First pilot', firstPilot ? title(firstPilot.name) + ' · started ' + String(firstPilot.pilot_started_at).slice(0, 10) : 'Not yet started', firstPilot ? 'started' : '-'],
        ['Reference participation', 'Committed in writing by founding partners', 'pending'],
        ['Outcome data rights', 'Part of the founding agreement', 'pending'],
      ],
    ],
    Health: [
      'Account health',
      hc && hc.customers.length
        ? 'Health is scored from each linked customer’s Atlas usage and outcomes, recomputed on every read.'
        : 'Health scoring needs usage and outcome data from a live account. With no customers yet there is nothing to score, so nothing is scored.',
      [
        ['Accounts scored', hc ? String(hc.customers.filter((x) => x.available).length) : '–', hc && hc.customers.length ? 'with live usage' : 'needs live usage', '#54606C'],
        ['At risk', hc ? String(hc.churnRisk) : '–', hc && hc.customers.length ? 'check in this week' : 'no accounts', hc?.churnRisk ? '#B3261E' : '#54606C'],
        ['Expansion ready', hc ? String(hc.expansionReady) : '–', hc && hc.customers.length ? 'propose beds or RPM' : 'no accounts', hc?.expansionReady ? '#1F6B52' : '#54606C'],
        ['Churn risk', hc && hc.customers.length ? String(hc.churnRisk) : '-', hc && hc.customers.length ? 'declining usage' : 'not measured', '#54606C'],
      ],
      [
        ['Signals used', 'Atlas engagement, alert acknowledgment, reimbursement captured', 'defined'],
        ['Scoring model', 'Scores linked customers from Atlas usage and outcomes', 'live'],
      ],
    ],
  };
  const panelDef = view === 'Customers' || view === 'Health' ? PANELS[view] : null;
  const panelsV = {
    crmPanelName: panelDef ? panelDef[0] : '',
    crmPanelNote: panelDef ? panelDef[1] : '',
    crmPanelTiles: panelDef ? panelDef[2].map((t) => ({ label: t[0], fig: t[1], note: t[2], figStyle: fig(t[3], 26) })) : [],
    crmPanelRows: panelDef ? panelDef[3].map((r) => ({ k: r[0], note: r[1], v: r[2], vStyle: 'font-size:16px;font-weight:700;color:#22272E;flex:0 0 auto;' })) : [],
  };

  // ── contact drawer (crmContact*, crmCt*) ──
  const isNew = contactId === 'new';
  const listed = !isNew && contactId ? contacts.find((x) => x.id === contactId) ?? null : null;
  const oneContact = useQuery((s) => crmContact(client, { params: { id: contactId! }, signal: s }), [client, tok, contactId], { enabled: !!contactId && !isNew && !listed });
  const contact = listed ?? oneContact.data?.contact ?? null;
  const ctCompany = useQuery((s) => crmCompany(client, { params: { facilityId: contact!.facility_id! }, signal: s }), [client, tok, contact?.facility_id], { enabled: !!contact?.facility_id });
  const ctActs = useQuery((s) => crmActivities(client, { query: { contactId: contact!.id, limit: 50 }, signal: s }), [client, tok, contact?.id], { enabled: !!contact });
  const ctDeals = contact?.facility_id ? allDeals.filter((d) => d.facility_id === contact.facility_id) : [];
  const ctName = isNew ? 'New contact' : contact?.name ?? '';
  const initials = (s: string) =>
    s
      .replace(/[^A-Za-z ]/g, '')
      .trim()
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0])
      .join('') || '#';
  const identity: Array<[string, string, string]> = [
    ['Name', 'name', contact?.name ?? ''],
    ['Title', 'title', contact?.title ?? ''],
    ['Email', 'email', contact?.email ?? ''],
    ['Phone', 'phone', contact ? phoneFmt(contact.phone) : ''],
  ];
  const saveContact = () => {
    if (!canWrite) return;
    const f = ctFields;
    if (isNew) {
      if (!f.name?.trim()) {
        setActErr('A contact needs a name.');
        return;
      }
      run(() => crmContactCreate(client, { body: { name: f.name.trim(), title: f.title || undefined, email: f.email || undefined, phone: f.phone || undefined } }), () => {
        setContactId(null);
        setCtEdit(false);
        people.reload();
      });
      return;
    }
    if (!contact || !Object.keys(f).length) {
      setCtEdit(false);
      return;
    }
    run(() => crmContactUpdate(client, { params: { id: contact.id }, body: f }), () => {
      setCtEdit(false);
      setCtFields({});
      people.reload();
    });
  };
  const co = ctCompany.data?.facility;
  const ctTimeline = ctActs.data?.activities ?? [];
  const contactV = {
    crmContactOpen: !!contactId && (isNew || !!contact),
    crmContactName: ctName,
    crmContactClose: () => {
      setContactId(null);
      setCtEdit(false);
      setCtConfirm(false);
      setCtFields({});
    },
    crmContactInitials: initials(ctName),
    // Sales-text consent for the contact's number (ContactSmsConsent, CrmExtras.tsx).
    crmCtId: !isNew && contact ? contact.id : null,
    crmCtPhoneKey: contact?.phone ?? '',
    crmCtWritable: canWrite,
    crmCtWriteTitle: canWrite ? undefined : writeReason ?? undefined,
    crmContactAvatar:
      'display:flex;align-items:center;justify-content:center;width:38px;height:38px;flex:0 0 38px;border-radius:10px;background:rgba(79,138,214,.14);color:#264C7E;font-size:16px;font-weight:700;',
    crmContactKind: isNew ? 'Fill in the identity and press Done' : contact?.kind ?? contact?.title ?? (contact?.role ? cap(contact.role.replace(/_/g, ' ')) : 'Contact'),
    crmCtEditing: ctEdit,
    crmCtNotEditing: !ctEdit,
    crmCtToggleEdit: () => (ctEdit ? saveContact() : canWrite && setCtEdit(true)),
    crmCtEditLabel: ctEdit ? 'Done' : 'Edit',
    crmCtEditStyle: btn(false) + (canWrite ? '' : disabledWrite),
    crmCtDeleteStyle:
      'display:inline-flex;align-items:center;height:30px;padding:0 13px;border-radius:8px;font-size:14px;font-weight:600;text-decoration:none;white-space:nowrap;color:#B3261E;background:rgba(179,38,30,.1);border:1px solid rgba(179,38,30,.34);' +
      (canWrite && !isNew ? '' : disabledWrite),
    crmCtDelete: () => canWrite && !isNew && setCtConfirm(true),
    crmCtConfirming: ctConfirm,
    crmCtCancelDelete: () => setCtConfirm(false),
    crmCtConfirmDelete: () =>
      contact &&
      run(() => crmContactArchive(client, { params: { id: contact.id }, body: {} }), () => {
        setContactId(null);
        setCtConfirm(false);
        people.reload();
        counts.reload();
      }),
    crmCtIdentity: identity.map((r) => ({
      label: r[0],
      val: r[2] || 'not captured',
      isEmpty: !r[2],
      valStyle: 'font-size:16px;font-weight:600;color:' + (r[2] ? '#22272E' : '#54606C') + ';',
      key: r[1],
      onInput: (e: ChangeEvent<HTMLInputElement>) => setCtFields((m) => ({ ...m, [r[1]]: e.target.value })),
      fieldVal: ctFields[r[1]] ?? (r[1] === 'phone' ? contact?.phone ?? '' : r[2]),
      placeholder: r[0] === 'Title' ? 'e.g. Director of Nursing' : r[0] === 'Email' ? 'name@facility.com' : r[0],
    })),
    crmCtIdentityNote:
      contact && !contact.title && !contact.email
        ? 'This row arrived as a facility main line from the CMS/NPPES record, so title and email are genuinely blank rather than guessed. Fill them in and they save against the contact.'
        : 'Edits save against the contact and are logged with who and when.',
    crmCtCompany: co ? co.name : contact?.facility_id ? '–' : 'No company linked',
    crmCtCompanyMeta: co
      ? [co.city ? co.city + ', ' + (co.state ?? '') : co.state, co.type, co.composite_score != null ? 'fit ' + Math.round(co.composite_score) + '/100' : null].filter(Boolean).join(' · ')
      : 'not linked to a facility in the catalog',
    crmCtCompanyOpen: () => {
      if (!contact?.facility_id) return;
      setRecId(contact.facility_id);
      setContactId(null);
    },
    crmCtDeals: ctDeals.map((d) => ({ name: title(d.name), stage: d.status === 'lost' ? 'Lost' : cap(d.stage_name), value: '-', note: 'started from ' + (sourceLabel[d.source ?? ''] ?? 'the CRM') })),
    crmCtNoDeals: ctDeals.length === 0,
    crmCtNoDealsHead: 'No deal linked to this contact',
    crmCtNoDealsNote: 'A deal links itself here the moment one is started against this company from Companies or Territory.',
    crmCtNoDealsNext: 'Start one from Companies → the contact picks it up',
    crmCtTimeline: ctTimeline.map((a) => ({
      kind: a.type,
      text: actText(a),
      when: ago(a.occurred_at ?? a.created_at, now),
      kindStyle: chip(a.type === 'note' ? '#54606C' : '#264C7E', a.type === 'note' ? 'rgba(34,39,46,.08)' : 'rgba(79,138,214,.16)'),
      isDone: !!a.completed_at,
      doneLabel: a.completed_at ? '✓ done' : 'Done',
      onDone: () => canWrite && run(() => crmActivityComplete(client, { params: { id: a.id }, body: { complete: !a.completed_at } }), () => ctActs.reload()),
      doneStyle: 'font-size:14px;text-decoration:none;white-space:nowrap;color:' + (a.completed_at ? '#1F6B52' : '#2F5C97') + ';',
    })),
    crmCtNoActivity: ctTimeline.length === 0,
    crmCtNoActivityHead: 'Nothing logged against this contact',
    crmCtNoActivityNote: 'A note is just an activity of kind note, so calls, emails, tasks and notes all land in this one timeline.',
    crmCtNoActivityNext: 'Log the first one above',
    crmCtDraft: ctDraft,
    crmCtSetDraft: (e: ChangeEvent<HTMLInputElement>) => setCtDraft(e.target.value),
    crmCtKinds: (['note', 'call', 'email', 'task'] as const).map((kd) => ({
      label: kd,
      onClick: () => setCtKind(kd),
      style:
        'display:inline-flex;align-items:center;height:24px;padding:0 10px;border-radius:6px;font-size:14px;text-decoration:none;white-space:nowrap;' +
        (ctKind === kd ? 'background:#2F5C97;color:#fff;font-weight:600;' : 'background:#FCFAF4;color:#3C454F;border:1px solid rgba(34,39,46,.16);'),
    })),
    crmCtAdd: () => {
      const t = ctDraft.trim();
      if (!t || !contact || !canWrite) return;
      run(() => crmActivityCreate(client, { body: { type: ctKind, subject: t, contactId: contact.id, facilityId: contact.facility_id } }), () => {
        setCtDraft('');
        ctActs.reload();
      });
    },
    crmCtAddStyle: btn(true) + (canWrite ? '' : disabledWrite),
  };

  // ── company record drawer (crmRec*) ──
  const rec = useQuery((s) => crmCompany(client, { params: { facilityId: recId! }, signal: s }), [client, tok, recId, recRefreshed], { enabled: !!recId });
  const recActs = useQuery((s) => crmActivities(client, { query: { facilityId: recId!, limit: 50 }, signal: s }), [client, tok, recId], { enabled: !!recId });
  const rf = rec.data?.facility;
  const recAll = recActs.data?.activities ?? [];
  const recContact = recAll.filter((a) => CONTACT_KINDS.includes(a.type));
  const recDeal = recId ? allDeals.find((d) => d.facility_id === recId && d.status === 'open') ?? allDeals.find((d) => d.facility_id === recId) ?? null : null;
  const aiRow = (k0: string, v0: string, tone: [string, string], src: string, why: string) => ({
    k: k0,
    v: v0,
    src,
    hasWhy: true,
    why,
    vStyle: 'font-size:16px;font-weight:700;color:' + tone[0] + ';background:' + tone[1] + ';border-radius:5px;padding:2px 8px;flex:0 0 auto;',
    srcStyle: chip(src === 'CRM' ? '#264C7E' : src === 'AITHORA' ? '#12503C' : '#5B6B7C', src === 'CRM' ? 'rgba(79,138,214,.16)' : src === 'AITHORA' ? 'rgba(47,138,111,.16)' : 'rgba(91,107,124,.18)'),
  });
  const tier = rec.data?.scoring?.tier ?? null;
  const tierTone: [string, string] = tier === 'A' ? ['#12503C', 'rgba(47,138,111,.16)'] : tier === 'B' ? ['#5B6B7C', 'rgba(91,107,124,.18)'] : ['#54606C', 'rgba(34,39,46,.08)'];
  const recV = {
    crmRecOpen: !!recId,
    crmRecName: rf ? rf.name : '',
    crmRecInitials: rf ? initials(rf.name) : '',
    crmRecAvatar:
      'display:flex;align-items:center;justify-content:center;width:38px;height:38px;flex:0 0 38px;border-radius:10px;background:rgba(79,138,214,.14);color:#264C7E;font-size:16px;font-weight:700;',
    crmRecMeta: rf ? [rf.city ? rf.city + ', ' + (rf.state ?? '') : rf.state, rf.type].filter(Boolean).join(' · ') : '',
    crmRecClose: () => {
      setRecId(null);
      setRecRefreshed(false);
    },
    crmRecRefresh: () => {
      setRecRefreshed(true);
      rec.reload();
    },
    crmRecRefreshLabel: recRefreshed ? 'Recomputed' : 'Recompute',
    crmRecRefreshStyle: btn(false) + 'height:26px;',
    crmRecAi: rec.data
      ? [
          aiRow('ICP fit', tier ? 'Tier ' + tier : 'Not scored', tierTone, 'AITHORA', 'Rubric: assisted living or skilled nursing, 100 to 300 beds, below the staffing median. This one is ' + (rf?.type ?? 'not typed') + ', which ' + (/^(AL|SNF)/.test(rf?.type ?? '') ? 'matches the setting' : 'does not match the setting') + '.'),
          aiRow('Fit score', rf?.composite_score != null ? Math.round(rf.composite_score) + ' / 100' : '–', ['#22272E', 'rgba(34,39,46,.07)'], 'AITHORA', 'The same composite shown in the Score column, recomputed weekly from CMS and the benchmark set.'),
          aiRow(
            'Reimbursement opportunity',
            rec.data.opportunity ? k(rec.data.opportunity.opportunityAnnual) + ' / yr' : '–',
            ['#12503C', 'rgba(47,138,111,.16)'],
            'CMS + MODEL',
            'Modeled Part B care-management dollars this facility is not capturing. Not booked revenue.',
          ),
          aiRow('Setting', rf?.type ?? '–', ['#22272E', 'rgba(34,39,46,.07)'], 'CMS', 'From the CMS provider record, not typed by a rep.'),
          aiRow('Status', rec.data.state?.status ?? (recDeal ? cap(recDeal.stage_name) : 'prospect'), ['#264C7E', 'rgba(79,138,214,.16)'], 'CRM', 'Changes when a rep moves the deal in My day or the board. Logged with who and when.'),
        ]
      : [],
    crmRecRel: (
      [
        ['First spoke', recContact.length ? String(recContact[recContact.length - 1].occurred_at ?? recContact[recContact.length - 1].created_at).slice(0, 10) : 'never'],
        ['Last spoke', recContact.length ? ago(recContact[0].occurred_at ?? recContact[0].created_at, now) : 'never'],
        ['Next step', recDeal ? NEXT[stageKey(recDeal.stage_name)] ?? 'not set' : 'not set'],
        ['Owner', recDeal ? firstName(memberList, recDeal.owner_id) : 'unassigned'],
        ['Emails exchanged', String(recAll.filter((a) => a.type === 'email').length)],
        ['Meetings', String(recAll.filter((a) => a.type === 'meeting' || a.type === 'booked').length)],
      ] as Array<[string, string]>
    ).map((x) => ({ k: x[0], v: x[1], vStyle: 'font-size:16px;font-weight:700;color:' + (recContact.length ? '#22272E' : '#54606C') + ';flex:0 0 auto;' })),
    crmRecRelNote: recContact.length
      ? 'Emails file here when a rep BCCs deals@aithoracare.com, and calls and meetings when they are logged.'
      : 'Emails file here when a rep BCCs deals@aithoracare.com. Nobody has emailed this account, so these stay empty rather than being guessed.',
    crmRecNoCall: true,
    crmRecNoCallNote: 'No call recorded on this account. Signals appear once a call is transcribed, and stay empty until then rather than being inferred.',
    crmRecSignals: [],
    crmRecActivity: recAll.length
      ? recAll.slice(0, 8).map((a) => ({ when: ago(a.occurred_at ?? a.created_at, now).replace(' ago', ''), what: actText(a), who: firstName(memberList, a.owner_id) + ' · ' + a.type }))
      : [{ when: '-', what: 'Nothing logged against this account yet', who: 'no email, call or meeting on record' }],
    crmRecStart: () => {
      if (!recId || recDeal || !canWrite) return;
      run(() => crmDealCreate(client, { body: { name: rf?.name ?? 'Facility', facilityId: recId, type: 'facility', track, source: 'catalog' } }));
    },
    crmRecStartLabel: recDeal ? 'Deal started' : 'Start deal',
    crmRecStartStyle: btn(!recDeal) + (canWrite ? '' : disabledWrite),
    crmRecLog: () => {
      if (!recId || !canWrite) return;
      run(() => crmFacilityCall(client, { params: { facilityId: recId }, body: { dealId: recDeal?.id } }), () => {
        setRecLogged(recId);
        recActs.reload();
      });
    },
    crmRecLogLabel: recLogged === recId ? 'Call logged' : 'Log a call',
    crmRecLogStyle: btn(false) + (canWrite ? '' : disabledWrite),
    crmRecProvenance: (() => {
      const e = recAll.find((a) => a.type === 'email');
      return e ? 'filed from deals@ inbox · ' + ago(e.occurred_at ?? e.created_at, now) : 'linked from the contact · no email filed yet';
    })(),
  };

  // ── toolbar ──
  const toolbar = {
    crmModeShow: isDealV,
    crmModes: ['Table', 'Board'].map((m) => ({
      n: m,
      onClick: () => setParam('crmMode', m === 'Table' ? null : m),
      style: 'display:inline-flex;align-items:center;height:28px;padding:0 10px;border-radius:6px;font-size:16px;font-weight:700;text-decoration:none;' + (mode === m ? 'background:#2F5C97;color:#fff;' : 'color:#3C454F;'),
    })),
    crmNewDeal: () => {
      setParam('crmV', 'Companies');
      setSavedOn(null);
      setFOpen(true);
    },
    crmQ: q,
    crmSetQ: (e: ChangeEvent<HTMLInputElement>) => setQ(e.target.value),
    crmQPh: isCat ? 'Ask in plain English: PE-backed SNFs in Texas, 100+ beds, 2+ penalties, no deal yet' : 'Find a deal, facility or person',
    crmQKey: (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && isCat) void parseInto();
    },
    crmAsk: () => void parseInto(),
  };

  const saved = views.data?.views ?? [];
  return {
    crmIsWs: true,
    crmIsToday: view === 'My day',
    crmIsDeals: isDealV && mode === 'Table',
    crmIsOther: other,
    crmIsPipeline: isDealV && mode === 'Board',
    crmIsTable: view === 'People',
    crmIsPanel: view === 'Customers' || view === 'Health',
    crmGridRef,
    crmGridStyle:
      'flex:1 1 auto;min-height:0;position:relative;display:grid;gap:12px;grid-template-columns:' +
      (narrow ? '138px minmax(0,1fr)' : showPanel ? '168px minmax(0,1fr) minmax(270px,330px)' : '168px minmax(0,1fr)') +
      ';',
    crmRail: RAIL_VIEWS.map((r) => ({ n: r, c: railCount[r], style: railS(view === r && !savedOn && !dealViewOn), onClick: () => goView(r) })),
    // Company views (the catalog) then deal views (crm-extras). A view opens with its own
    // filters and sort; a deal view also brings its columns.
    crmSavedList: [
      ...saved.filter((sv) => (sv.object ?? 'company') === 'company').map((sv) => ({
        n: sv.name,
        scope: (sv.is_shared ? 'Team' : 'Mine') + (sv.is_default ? ' ·' : ''),
        style: railS(savedOn === sv.id),
        onClick: () => applyCompanyView(sv),
      })),
      ...dealViewList.map((sv) => ({
        n: sv.name,
        scope: (sv.is_shared ? 'Team' : 'Mine') + (sv.is_default ? ' ·' : ''),
        style: railS(dealViewOn === sv.id),
        onClick: () => applyDealView(sv),
      })),
    ],
    // crm-extras: what the bulk, view and inline-edit controls need.
    crmClient: client,
    crmCanWrite: canWrite,
    crmUserId: userId ?? null,
    crmIsManager: isManager,
    crmMembers: memberList ?? [],
    cIsOps: isCat && cKind === 'Operators',
    ...toolbar,
    ...mx,
    ...myday,
    ...panel,
    ...dealsV,
    ...board,
    ...catalogV,
    ...peopleV,
    ...panelsV,
    ...contactV,
    ...recV,
    loading: myDay.loading && !myDay.data,
    loadError: myDay.error ?? deals.error ?? counts.error,
    reloadAll: () => {
      reloadDeals();
      pipes.reload();
    },
  };
}
